/**
 * The model behind the items-page filter builder: the vocabulary of fields a
 * collection offers, the operators each dataType can use, and the translation
 * between builder rows and the KQL AST.
 *
 * Everything here is pure — no React, no antd — so the UI (and Phase 4's saved
 * filters) can compose queries without duplicating grammar knowledge. The AST
 * shape itself comes from the vendored module in ./kql; nothing in this file
 * hand-builds a node literal.
 */
import dayjs from 'dayjs';
import { parse, serialize, cmp as makeCmp, list as makeList, str as makeStr, and as makeAnd, not as makeNot, has as makeHas, me as makeMe, ref as makeRef, isRelativeLiteral, usesMe, SYSTEM_FIELDS, DATE_PARTS, CREATOR_PART, WEEKDAYS, datePartNumber, elementIdOf, isElementIdSegment, ELEMENT_ID_RE, isBlock, blockOf, BLOCK_CMP, ENTRY_COUNT } from './kql';
import { isHiddenFromMe } from './elementAccess';
import { columnsOf } from '../config/repeatingGroups';
import { getTimePattern } from './dateDisplay';
import { normalizeId } from './ids';
import { ME_VALUE, isIdentityElement } from './userElements';
import { FACET_KIND, FACET_OPERATORS, FACET_PREFIX, buildFacetFields, facetFieldKey, isFacetField } from './facets';
import { isMeasurementValue, isMultiMeasure, isOwnUnitId, ownUnitOf } from './units';
import { formatBooleanValue } from './elements';
import { formatEnteredMeasurement, readEntryValue } from './measurements';
import { allowedCodes, isCurrencyValue, parseCurrency } from './currency';
import { currencyLiteral, formatEnteredCurrency } from './money';
import { TAG_FIELD_KEY, tagValueLabel } from './tagNames';
import { choiceListOf } from './choiceValues';

/** System fields the builder offers as rows. `card` stays with the card picker. */
export const SYSTEM_FILTER_FIELDS = [
	{ key: 'created', path: ['created'], label: 'Created', dataType: 'date-time', system: true },
	{ key: 'updated', path: ['updated'], label: 'Updated', dataType: 'date-time', system: true },
	// The card-level Notes field: a long text the grammar treats exactly like
	// any other text (contains / is / is empty).
	{ key: 'notes', path: ['notes'], label: 'Notes', dataType: 'text-large', system: true },
];

const SYSTEM_FIELD_SET = new Set(SYSTEM_FIELDS);

/**
 * REPEATING GROUPS (KPR-683; keepr-api docs/SCHEMA.md "Repeating groups in
 * KQL"). A group is asked four ways — `g has (conditions)`, `not g has
 * (conditions)`, `g is empty`, `g is not empty` — and its `g.count` is a
 * number. A group's field offers the four (kind `entries`); its count is a
 * field of its own (dataType `entry-count`, path `[g, 'count']`). A `has` row
 * holds NESTED rows over the group's columns, all of which must hold on ONE
 * entry (`entryRows`); the block is built from them on the way out and read
 * back into them on the way in, and a block whose inside is more than an AND of
 * simple conditions (an `or`, a `not`) stays the Advanced tab's.
 */
export const REPEATING_GROUP_TYPE = 'repeating-group';
export const ENTRIES_KIND = 'entries';
export const ENTRY_COUNT_TYPE = 'entry-count';
/** True for a repeating group's field. */
export const isEntriesField = field => field?.dataType === REPEATING_GROUP_TYPE;
/** True for a group's entry-count field. */
export const isEntryCountField = field => field?.dataType === ENTRY_COUNT_TYPE;
/** The two comparisons that carry a block: `has an entry where…` and `has no entry where…`. */
export const isEntriesCmp = comparison => comparison === 'has' || comparison === 'nhas';

/**
 * DATE PARTS AND THE CREATOR (keepr-api PR AK, docs/SCHEMA.md § KQL "Date
 * parts and the creator"). `created.weekday = mon`, `created.hour >= 9`,
 * `due.month = 3` and `created.by = me` are items-list questions the API
 * answers in the collection's time zone; a chart's day-of-week, hour-of-day,
 * month-of-year and "Created by" bars open lists filtered by them. The builder
 * offers each part of `created`, `updated` and every date or date-time
 * element as a field of its own — a picker of the part's values, in the
 * part's order (Sunday first, as weeks are) — and "Created by" as a system
 * field taking a person, like a user element. The grammar's vocabulary is
 * the vendored kql.js (`datePartNumber` reads a typed `Mon` or `monday` as
 * the API does); the words are here.
 */
export const DATE_PART_TYPE = 'date-part';
const DATE_PART_WORDS = { weekday: 'Day of the week', hour: 'Hour of the day', month: 'Month' };
const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// The parts each kind of date has: a date carries no clock.
const PARTS_OF_TYPE = { date: ['weekday', 'month'], 'date-time': ['weekday', 'hour', 'month'] };

/** "Created by" — who added an item (`created.by`), picked like a user element. */
export const CREATOR_FILTER_FIELD = { key: `created.${CREATOR_PART}`, path: ['created', CREATOR_PART], label: 'Created by', dataType: 'user', system: true };

/** A part's label for one of its values (a weekday 0–6, an hour 0–23, a month 1–12), the hour in the viewer's time pattern. */
function datePartValueLabel(part, n) {
	if (part === 'weekday') return WEEKDAY_LABELS[n];
	if (part === 'month') return MONTH_LABELS[n - 1];
	return dayjs(new Date(2000, 0, 1, n, 0)).format(getTimePattern());
}

/** The picker's choices for a part: each value as the canonical literal the query carries (`mon`, 9, 3). */
function datePartChoices(part) {
	if (part === 'weekday') return WEEKDAYS.map((value, n) => ({ value, label: WEEKDAY_LABELS[n] }));
	const [lo, hi] = part === 'hour' ? [0, 23] : [1, 12];
	const out = [];
	for (let n = lo; n <= hi; n++) out.push({ value: n, label: datePartValueLabel(part, n) });
	return out;
}

/** True for the builder's field of a date part. */
export const isDatePartField = field => field?.dataType === DATE_PART_TYPE;

/** The field of one part of a date field (`root`: a one-segment date or date-time field). */
export function datePartField(root, part) {
	const path = [root.path[0], part];
	return {
		key: pathKey(path, !!root.forceElement),
		path,
		forceElement: !!root.forceElement,
		label: `${root.label} · ${DATE_PART_WORDS[part]}`,
		dataType: DATE_PART_TYPE,
		// The root's own type, for the type glyph in the field menu.
		badgeType: root.dataType,
		options: { part, choices: datePartChoices(part), quickFilter: false },
		cardLabels: root.cardLabels || [],
		cardIds: root.cardIds || [],
		partial: !!root.partial,
		system: !!root.system,
	};
}

/** Every date part the given fields have: each part of each one-segment date or date-time field. */
export function datePartFilterFields(fields = []) {
	const out = [];
	(fields || []).forEach(field => {
		if (!field || field.facet || field.hop || !Array.isArray(field.path) || field.path.length !== 1) return;
		(PARTS_OF_TYPE[field.dataType] || []).forEach(part => out.push(datePartField(field, part)));
	});
	return out;
}

/** The label of a date part's value — typed (`Mon`, "monday", 3) or picked — or undefined when the part reads none. */
export function datePartLabelFor(field, raw) {
	if (!isDatePartField(field) || raw === undefined || raw === null || raw === '') return undefined;
	const part = field.options?.part;
	const n = datePartNumber(part, typeof raw === 'number' ? { type: 'num', v: raw } : { type: 'str', v: String(raw) });
	return n === null ? undefined : datePartValueLabel(part, n);
}

/**
 * THE `tags` FIELD (docs/plans/tags.md §3.7, §5.2; keepr-api § Querying tags).
 * A system field whose value is a TAG: the row gets the tag picker
 * (components/tags/TagSelect — a tree that writes ids and shows names), the
 * chip reads the tag's name, and the builders check a typed name, alias or
 * path against what it could mean (utils/idValueValidation.js, the `tag`
 * kind; ruled d21).
 *
 *   tags               the collection's vocabulary (kind-`tag` tags, its own
 *                      and its parent's — useCollectionTags)
 *   privateTags        my private tags (TagsContext); a `collection`-mode
 *                      field still carries them, to SAY that one is private
 *   itemTitles         item-tag titles the page holds (`{ id: { title } }`)
 *   mode               'live' (a reader's own list, a personal filter: private
 *                      tags too), 'collection' (stored and shared — pinned
 *                      filters, rules, checks: ids only, never a private tag)
 *                      or 'access' (T7: what decides who sees what — an access
 *                      level's filter, a dynamic group's rule, a collection
 *                      link's or a public link's filter: RESTRICTED collection
 *                      tags only, by id, and never emptiness —
 *                      keepr-api § Querying tags, `access` mode). `tags` still
 *                      holds the whole vocabulary there, so the check can say
 *                      why an unrestricted tag can't be used; the picker offers
 *                      the restricted ones alone.
 *   loaded             the vocabulary has arrived (the check waits for it)
 *   quickFilter        a Tags chip on the toolbar (there is something to pick)
 *
 * `is empty` reads the two collection-level arrays only (ruled d14): a
 * person's private tagging is not a tag to anyone else.
 */
const TAG_FIELD_MODES = new Set(['live', 'collection', 'access']);

/** Is this the `tags` field in ACCESS mode (restricted tags only, T7)? */
export const isAccessTagsField = field => field?.dataType === 'tag' && field?.options?.mode === 'access';

export function tagsFilterField({
	tags = [],
	privateTags = null,
	itemTitles = null,
	mode = 'live',
	loaded = false,
	quickFilter = false,
} = {}) {
	return {
		key: TAG_FIELD_KEY,
		path: [TAG_FIELD_KEY],
		forceElement: false,
		label: 'Tags',
		shortLabel: 'Tags',
		dataType: 'tag',
		system: true,
		cardLabels: [],
		cardIds: [],
		partial: false,
		options: {
			tags: Array.isArray(tags) ? tags : [],
			privateTags: Array.isArray(privateTags) ? privateTags : [],
			itemTitles: itemTitles || null,
			mode: TAG_FIELD_MODES.has(mode) ? mode : 'live',
			loaded: Boolean(loaded),
			quickFilter: Boolean(quickFilter),
		},
	};
}

/**
 * The choices of a REFERENCE field (`card`, `set`): one per record, by ID —
 * the value a filter names it by since deterministic filters (owner ruling
 * d19; docs/plans/deterministic-filters.md §3.7): a key moves, an id does not.
 * Each choice keeps the record's key as an ALIAS, so text written before F3
 * (`set = contact`) still reads as the record's name and the value check can
 * offer to write the id instead. Two records that share a name are told apart
 * by tier ("Contact · global"); nothing is deduplicated by key, since a
 * global set and a collection set sharing one are two different records.
 */
function referenceChoices(records = [], { aliasPool = null, aliasable = null } = {}) {
	// A key is an alias only when ONE record of the whole pool has it
	// (`aliasPool`, default `records`) and the record is one the API's
	// vocabulary here holds (`aliasable`, default every record): which record
	// text written before F3 meant by a key two share, or by a key of a record
	// the API does not consider here, is the server's to say, never this
	// page's guess (the F3 review's F1/F2).
	const keyCount = new Map();
	(aliasPool || records || []).forEach(record => {
		const key = record?.key ? String(record.key).toLowerCase() : '';
		if (key) keyCount.set(key, (keyCount.get(key) || 0) + 1);
	});
	const named = new Map();
	(records || []).forEach(record => {
		const name = record?.name || record?.key || '';
		named.set(name, (named.get(name) || 0) + 1);
	});
	const seen = new Set();
	const choices = [];
	(records || []).forEach(record => {
		const value = normalizeId(record?._id);
		if (!value || seen.has(value)) return;
		seen.add(value);
		const name = record?.name || record?.key || value;
		const shared = (named.get(record?.name || record?.key || '') || 0) > 1;
		choices.push({
			value,
			label: shared && record?.scope === 'global' ? `${name} · global` : name,
			...(record?.key && keyCount.get(String(record.key).toLowerCase()) === 1 && (!aliasable || aliasable(record)) ? { aliases: [String(record.key)] } : {}),
		});
	});
	return choices;
}

/**
 * The `set` system field: the element set an item's card carries (`set =
 * <id>`, `set in (…)`, `set is empty` — keepr-api utils/kql/compile.js
 * compileSet). Offered as a CHOICE over the sets the caller can see, so the
 * row gets a picker and the chip reads the set's NAME; the value is the
 * set's id (referenceChoices). Nothing when there are no sets to offer.
 */
export function elementSetFilterField(elementSets = [], { keepKeys = false } = {}) {
	const choices = referenceChoices(elementSets);
	if (!choices.length) return null;
	return { key: 'set', path: ['set'], label: 'Element set', dataType: 'choice', options: { choices }, system: true, reference: 'set', ...(keepKeys ? { keepKeys: true } : {}) };
}

/**
 * The `card` system field — NOT offered in the field menu (`notOffered`: the
 * toolbar's card picker owns that question), but known, so a `card = <id>`
 * condition reads as the card's name on a chip and in a row, and the value
 * check can judge it (a card no longer here; a key written before F3).
 */
export function cardFilterField(cards = [], { cardIds = null, keepKeys = false } = {}) {
	// Every readable card is NAMED (an id reads as its card wherever it is
	// from). A KEY is an alias — read as a card, and written as its id on
	// Apply — only for a card of THIS collection's vocabulary (`cardIds`, the
	// caller's, plus the globals) whose key no other card of that vocabulary
	// has: the cards the API reads a key against here. Another collection's
	// card is never what a key means (the F3 review's F1); without a
	// vocabulary no key is an alias.
	const pool = vocabularyCards(cards, cardIds);
	const inPool = new Set((pool || []).map(card => normalizeId(card?._id)));
	const choices = referenceChoices(cards, {
		aliasPool: pool || [],
		aliasable: card => inPool.has(normalizeId(card?._id)),
	});
	if (!choices.length) return null;
	return { key: 'card', path: ['card'], label: 'Card type', dataType: 'choice', options: { choices }, system: true, reference: 'card', notOffered: true, ...(keepKeys ? { keepKeys: true } : {}) };
}

/**
 * The cards of a collection's vocabulary — those `cardIds` names, and the
 * globals — or null when the caller has no vocabulary. What a key is counted
 * and read against (the card field's aliases, a facet's).
 */
export function vocabularyCards(cards = [], cardIds = null) {
	if (!(cardIds instanceof Set)) return null;
	return (cards || []).filter(card => cardIds.has(normalizeId(card?._id)) || card?.scope === 'global');
}

/**
 * dataType → the operator family it belongs to. Anything unrecognized is text,
 * which mirrors the API compiler's own fallback.
 */
export function fieldKindFor(dataType) {
	switch (dataType) {
		// A "Me too" count (KPR-390) filters as a number — is, more than, less
		// than, is empty — and never `contains` (the API refuses `~` on one).
		// The server reads its blank as 0 (`me-too < 3` finds the items
		// nobody confirmed, `is empty` is "nobody yet"); the builder has
		// nothing to translate for that.
		case 'number':
		case 'decimal':
		case 'integer':
		case 'rating':
		case 'confirmations':
			return 'number';
		case 'date':
		case 'date-time':
		case 'time':
			return 'date';
		case 'choice':
			return 'choice';
		case 'card-lookup':
			return 'lookup';
		case 'measurement':
			return 'measurement';
		// Money compares within one currency server-side (docs/plans/
		// currency.md "Query and sort"), so its literals always carry a code.
		case 'currency':
			return 'currency';
		case 'boolean':
			return 'boolean';
		// A tag is picked from the tree (an id); a typed name is checked.
		case 'tag':
			return 'tag';
		// A user value is an account id; the API matches it exactly (or, on
		// `me`, against the caller) and never resolves a name — so the row
		// gets the account picker and no free text (docs/SCHEMA.md § KQL).
		case 'user':
			return 'user';
		// A file element (PR 10b) filters only on whether it holds anything:
		// its value is an attachment id, which nobody types, and file names
		// are not searched (the 10b contract: `is empty` / `not empty` only).
		case 'file':
			return 'file';
		// A part of a date (`created.weekday`, PR AK): picked from its values.
		case DATE_PART_TYPE:
			return 'datePart';
		// A repeating group (KPR-683): asked whether it has an entry that
		// answers some conditions, has none, or has any at all; its entry
		// count is a number of its own.
		case REPEATING_GROUP_TYPE:
			return ENTRIES_KIND;
		case ENTRY_COUNT_TYPE:
			return 'entryCount';
		// The three text-adjacent types all filter as TEXT (docs/plans/
		// data-types.md decision 1): a stored phone is a string, an email is
		// a string, and a location is filtered over its address — the API's
		// compiler appends `.address` to the element path itself, so the
		// builder emits the plain element and never a sub-path the user
		// would then see in a chip.
		case 'phone':
		case 'email':
		case 'location':
			return 'text';
		default:
			return 'text';
	}
}

/**
 * The operator family of a FIELD — the one entry point for every question
 * about a field a person sees: its operators, its value control, its chip's
 * words, its quick filter (KPR-142). `fieldKindFor(dataType)` stays for the
 * callers that have only a stored type, and for the questions that ARE about
 * the stored type: the API's field-to-field matrix (`refFamilyFor`), the value
 * coercions (`makeValueNode`), who may take `me`.
 *
 * A field is a choice when its type is Choice, and only then (the owner's
 * ruling, 2026-10-19; KPE-033, KPR-1015). KPR-142 also read a `text-small`
 * carrying `options.choices` as a choice; that legacy construct was migrated
 * away (KPR-1012/1013), and a list left on any other type is inert — kept for
 * a change back to Choice, never read (utils/choiceValues `rendersAsChoice`).
 */
export function fieldKindOf(field) {
	return fieldKindFor(field?.dataType);
}

/** Human words for a comparison — used by both operator selects and chips. */
export const OPERATOR_LABELS = {
	eq: 'is',
	ne: 'is not',
	gt: '>',
	gte: '>=',
	lt: '<',
	lte: '<=',
	contains: 'contains',
	ncontains: 'does not contain',
	in: 'is any of',
	nin: 'is none of',
	empty: 'is empty',
	nempty: 'is not empty',
	// A repeating group's block (KPR-683): `g has (…)` and its negation.
	has: 'has an entry where',
	nhas: 'has no entry where',
};

/** What a repeating group's emptiness reads as (a group is "empty" when it has no entries). */
const ENTRIES_OPERATOR_LABELS = { empty: 'has no entries', nempty: 'has entries' };

/**
 * A Choice that allows multiple values ("Allow multiple", docs/plans/data-
 * types-second-pass.md §3.5) holds a LIST, and the API answers a condition
 * entry by entry: `=` and `in` when ANY entry matches, every negation when NO
 * entry does (keepr-api docs/SCHEMA.md § KQL). The words say so — "has no
 * Welding" is what `skills != welding` means; "is not Welding" would promise
 * that an item holding welding AND rigging matches, and it does not.
 */
const LIST_OPERATOR_LABELS = {
	eq: 'has',
	ne: 'has no',
	in: 'has any of',
	nin: 'has none of',
};

/** Does this field hold a list of choices? */
export const fieldHoldsChoiceList = field => field?.dataType === 'choice' && field?.options?.allowMultiple === true;

/** The words for a comparison ON THIS FIELD — its list reading where it holds one. */
export function operatorLabelFor(field, cmp) {
	if (fieldKindOf(field) === ENTRIES_KIND && ENTRIES_OPERATOR_LABELS[cmp]) return ENTRIES_OPERATOR_LABELS[cmp];
	if (fieldHoldsChoiceList(field) && LIST_OPERATOR_LABELS[cmp]) return LIST_OPERATOR_LABELS[cmp];
	return OPERATOR_LABELS[cmp] || cmp;
}

const OPERATORS_BY_KIND = {
	text: ['eq', 'ne', 'contains', 'ncontains', 'empty', 'nempty'],
	number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'empty'],
	// `is not empty` earns its place on a date: "Overdue" is "before today AND
	// has one at all", and a date nobody filled in is not overdue.
	date: ['gt', 'gte', 'lt', 'lte', 'eq', 'empty', 'nempty'],
	// Membership plus emptiness: "root cards only" is `parent is empty`, and a
	// choice element nobody has filled in is `status is empty`.
	choice: ['eq', 'ne', 'in', 'nin', 'empty', 'nempty'],
	// A picked item, several (`in`, KPR-1712, given a card to pick from) or a title the API resolves; no order.
	lookup: ['eq', 'in', 'contains', 'empty'],
	// A quantity compares on its canonical base server-side; `~` matches
	// nothing there, and a list of quantities is not something anyone types.
	measurement: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'empty', 'nempty'],
	// Money: the same set. The API also reads `in` / `not in` over amounts,
	// which a typed query may use; the builder offers LESS on purpose — a list
	// of prices is not something anyone picks from a row.
	currency: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'empty', 'nempty'],
	// Two states and an absence: a yes/no has nothing to be greater than, and
	// a list of two is the same question asked twice.
	boolean: ['eq', 'ne', 'empty'],
	// An account is picked or it is not: equality (the caller included, as
	// `me`), a list of people (`in` / `not in`, KPR-996) and emptiness. A
	// reference has no substring and no order.
	user: ['eq', 'ne', 'in', 'nin', 'empty', 'nempty'],
	// A file: held or not (PR 10b).
	file: ['empty', 'nempty'],
	// A repeating group (KPR-683): an entry that answers every condition under
	// it ('has an entry where…'), none that does, or none at all / some.
	[ENTRIES_KIND]: ['has', 'nhas', 'empty', 'nempty'],
	// How many entries it holds: a number, never blank (`group.count`).
	entryCount: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
	// A part of a date: one of its values, a list of them, a side of one in
	// the part's order (`created.hour >= 9`), or none readable.
	datePart: ['eq', 'ne', 'in', 'nin', 'gt', 'gte', 'lt', 'lte', 'empty', 'nempty'],
	// A tag is membership and emptiness: `is` / `is any of` (a parent
	// includes the tags beneath it), their negations, and `is empty` — which
	// reads the item's own tags, never a person's private ones (ruled d14).
	tag: ['eq', 'ne', 'in', 'nin', 'empty', 'nempty'],
	// A facet is membership and emptiness only — the frozen wire contract
	// (docs/plans/card-editor-dates-groups-filters.md Part 5, "Wire contract").
	[FACET_KIND]: FACET_OPERATORS,
};

/**
 * The comparisons the filter builder offers a KIND of field (`fieldKindFor`'s
 * answer), in the order it offers them. The condition builder derives its own
 * set from this table (utils/conditionOperators.js), so the two cannot drift.
 */
export const operatorValuesForKind = kind => [...(OPERATORS_BY_KIND[kind] || OPERATORS_BY_KIND.text)];

/** Operator options (antd Select shape) for a family of fields (`fieldKindFor` / `fieldKindOf`'s answer). */
const operatorsForKind = kind => (OPERATORS_BY_KIND[kind] || OPERATORS_BY_KIND.text)
	.map(value => ({ value, label: OPERATOR_LABELS[value] }));

/** Operator options (antd Select shape) legal for a stored dataType alone. */
export function operatorsForDataType(dataType) {
	return operatorsForKind(fieldKindFor(dataType));
}

/**
 * Operator options for a FIELD. A facet wears `dataType: 'card-lookup'` so it
 * gets the item picker and the title-resolving chip for free, but its
 * operators are its own — so every caller that has the descriptor asks here,
 * and only the ones that have nothing but a type fall back to the type.
 */
export function operatorsForField(field) {
	if (isFacetField(field)) {
		return FACET_OPERATORS.map(value => ({ value, label: OPERATOR_LABELS[value] }));
	}
	// What decides who sees what names a restricted tag, never an absence
	// (T7: `is empty` in an access query is the API's 400
	// `unsupported_operator`).
	if (isAccessTagsField(field)) {
		return ACCESS_TAG_OPERATORS.map(value => ({ value, label: OPERATOR_LABELS[value] }));
	}
	const titleBox = fieldKindOf(field) === 'lookup' && !field?.options?.lookupCardId; // holds one title, never a list
	return operatorsForKind(fieldKindOf(field)).filter(option => !titleBox || option.value !== 'in').map(option => ({ ...option, label: operatorLabelFor(field, option.value) }));
}

/**
 * The operator options of ONE ROW: its field's (`operatorsForField`), plus the
 * comparison the row already holds when the field no longer offers it (KPR-142).
 * A filter saved while an element read as text may say `status contains "prog"`;
 * a choice is never offered `contains`, but the row still shows and applies it:
 * nothing rewrites a stored filter because the builder learned more. Carried
 * this way: `contains` / `does not contain` on a choice, and (KPR-1640) the
 * negations and emptiness the API reads on every type, which a cell's Exclude writes.
 */
export function operatorsForRow(field, cmp) {
	const options = operatorsForField(field);
	const carried = (fieldKindOf(field) === 'choice' && isTextMatchOperator(cmp)) || (['ne', 'nin', 'empty', 'nempty'].includes(cmp) && !isAccessTagsField(field));
	return carried && !options.some(option => option.value === cmp) ? [...options, { value: cmp, label: operatorLabelFor(field, cmp) }] : options;
}

const ACCESS_TAG_OPERATORS = ['eq', 'ne', 'in', 'nin'];

/**
 * THE OPERATORS THE API REFUSES ON `tags` (keepr-api § Querying tags; ruled
 * b26, T7): a tag is matched by is / is not / is any of / is none of (and,
 * outside access, is empty / is not empty) — never by part of its name
 * (`~`, `contains`, `!~`) or by order (`<` `<=` `>` `>=`), which every
 * writer and a live `?q=` answer 400 `unsupported_operator`; and an access
 * query never asks about emptiness. The builders never offer these; a query
 * TYPED (the Advanced tab, a condition edited as text) or loaded with one is
 * flagged where it is, and the filter builder holds Apply.
 */
export const TAG_OPERATOR_REFUSAL = 'Tags can only be matched by is or is not.';
export const ACCESS_TAG_EMPTY_REFUSAL = 'Who sees what can’t depend on whether an item has tags. Pick the restricted tags to match instead.';

/** The sentence a condition's operator is refused in, or null when the API takes it. */
export function refusedOperatorMessage(field, comparison) {
	if (fieldKindOf(field) !== 'tag' || !comparison) return null;
	if (!OPERATORS_BY_KIND.tag.includes(comparison)) return TAG_OPERATOR_REFUSAL;
	if (isAccessTagsField(field) && isNullaryOperator(comparison)) return ACCESS_TAG_EMPTY_REFUSAL;
	return null;
}

/**
 * The kinds whose value may be the caller: a user element (`assignee = me`,
 * the caller's account) and a lookup or a facet (`resident = me`, `@people =
 * me`, the caller's LINKED ITEM in the collection). The grammar's `me` is a
 * value keyword like `today`; the text "me" is `"me"` (kql.js).
 */
const ME_KINDS = new Set(['user', 'lookup']);

/** May a field of this dataType take `me` as a value? */
export const fieldTakesMe = dataType => ME_KINDS.has(fieldKindFor(dataType));

// ----------------------------------------------------- element references

/**
 * A row's value may be a LITERAL or a REFERENCE to another field of the same
 * record (`on-hand <= {{reorder-at}}`). The raw form a row's input binds to is
 * TAGGED, so nothing downstream can mistake an element name for a typed-in
 * string: `{ kind: 'ref', name: 'reorder-at' }`.
 */
export const REF_KIND = 'ref';
export const makeRefRaw = name => ({ kind: REF_KIND, name: String(name) });
export const isRefRaw = raw => !!raw && typeof raw === 'object' && raw.kind === REF_KIND && typeof raw.name === 'string';

/** The element name a value node references, or null. */
export const refNameOf = value => (value && value.type === 'ref' ? value.v : null);

// The comparison families, MIRRORED from the API compiler
// (keepr-api utils/kql/compileValues.js REF_FAMILY_BY_TYPE / REF_CMPS). The
// builder must only ever offer a pair the compiler will actually compare, AND
// only an operator that pair has a reading for: there is no warning channel on
// `?q=`, so a query built here can never be allowed to hit the no-match. The
// API refuses both halves at write time (refCmpProblem), and an unanswerable
// reference compiles to a no-match in BOTH polarities there, so a query this
// file lets through would come back as a correct-looking empty list.
//
// The two matrices are hand-copied across repos and
// __tests__/kqlBuilderRefs.test.js pins the direction that can hurt: every
// operator offered here folds to one the API compiles. Offering LESS is fine
// and deliberate — there is no `>` on text here, which reads as nothing
// anybody means to ask.
const REF_FAMILY_BY_TYPE = {
	number: 'number', decimal: 'number', integer: 'number', rating: 'number',
	date: 'date', 'date-time': 'date',
	// A wall clock is not a calendar date.
	time: 'time',
	measurement: 'measurement',
	// Two currency elements compare their amounts only when both rows hold
	// the SAME currency (the API's `currency` family).
	currency: 'currency',
	boolean: 'boolean',
	location: 'location',
	'text-small': 'text', 'text-large': 'text', url: 'text', choice: 'text', phone: 'text', email: 'text',
};

// A reference against an account or an item reference reads as "the sets
// intersect", which allowMultiple makes a design of its own. v1: no target.
// A file is compared with nothing (PR 10b): its ids name attachments.
// Nor is a "Me too" count (KPR-390): a `{{reference}}` on either side cannot
// answer on the server (keepr-api SCHEMA.md § Compilation semantics).
const REF_UNSUPPORTED_TYPES = new Set(['user', 'card-lookup', 'file', 'confirmations']);

const REF_CMPS_BY_FAMILY = {
	number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
	measurement: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
	currency: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
	date: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
	time: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
	text: ['eq', 'ne', 'contains', 'ncontains'],
	location: ['eq', 'ne', 'contains', 'ncontains'],
	boolean: ['eq', 'ne'],
};

/** The reference family a dataType belongs to, or null when it has none. */
export function refFamilyFor(dataType) {
	const type = dataType || '';
	if (REF_UNSUPPORTED_TYPES.has(type)) return null;
	return REF_FAMILY_BY_TYPE[type] || 'text';
}

/** Does this operator have a reading when the right-hand side is a field? */
export function refSupportsOperator(dataType, comparison) {
	const family = refFamilyFor(dataType);
	if (!family) return false;
	return (REF_CMPS_BY_FAMILY[family] || []).includes(comparison);
}

const refAllowsMultiple = field => field?.options?.allowMultiple === true;

/**
 * May this field be compared against that one? The API's type matrix, plus
 * the three shapes that have no reading at all (a facet names a card, a system
 * field holds no element, a dot-walk leaf would read the OTHER record's
 * document). A field compared with itself is excluded: the answer is "yes,
 * unless blank" for every row, which is not a question anybody means to ask.
 */
export function canReferenceField(field, target) {
	if (!field || !target) return false;
	if (field.facet || target.facet) return false;
	if (field.system || target.system) return false;
	if ((field.path || []).length !== 1 || (target.path || []).length !== 1) return false;
	if (field.key === target.key) return false;
	const left = refFamilyFor(field.dataType);
	const right = refFamilyFor(target.dataType);
	if (!left || !right || left !== right) return false;
	if (left === 'measurement' && (!field.options?.measure || field.options.measure !== target.options?.measure)) return false;
	// An element that allows several measures (KPR-1085) may hold a different
	// measure on each item: the API refuses it on either side.
	if (left === 'measurement' && (isMultiMeasure(field.options || {}) || isMultiMeasure(target.options || {}))) return false;
	// Two currency elements whose lists share no currency could never hold
	// the same one on a row: the API refuses the pair.
	if (left === 'currency') {
		const mine = allowedCodes(field.options);
		const theirs = allowedCodes(target.options);
		if (!mine.some(code => theirs.includes(code))) return false;
	}
	if (refAllowsMultiple(field) && refAllowsMultiple(target)) return false;
	return true;
}

/**
 * The fields this one may be compared against — the option list of the row's
 * "another field" picker. Empty means the toggle is not offered at all, which
 * is how the builder guarantees it can never author a condition that compiles
 * to a no-match.
 */
export function refTargetsFor(field, fields) {
	return (fields || []).filter(target => canReferenceField(field, target));
}

/** Is this raw (builder-side) value the caller? */
export const isMeValue = raw => raw === ME_VALUE;

/**
 * Does this list's vocabulary carry an ACCOUNT LINK — a user element with
 * `options.identity` on one of the collection's own cards? Then a lookup row
 * or chip may be asked for "me" (the caller's linked record). Read off the
 * FIELDS the page built from the collection's member cards, never off the
 * app-wide card cache: that cache holds every readable collection's cards,
 * and another collection's link is no reason to offer Me here.
 */
export const fieldsHaveAccountLink = fields => (fields || []).some(field => (
	!field?.system && !field?.facet && isIdentityElement({ dataType: field?.dataType, options: field?.options })
));

/** Does the query name the caller anywhere? (A share link cannot carry one.) */
export function queryUsesMe(text) {
	const trimmed = (text || '').trim();
	if (!trimmed) return false;
	try {
		return usesMe(parse(trimmed));
	} catch (err) {
		return false;
	}
}

/** Comparisons that carry no value at all. */
export const isNullaryOperator = comparison => comparison === 'empty' || comparison === 'nempty';
/** Comparisons whose value is a list. */
export const isListOperator = comparison => comparison === 'in' || comparison === 'nin';

/** `contains` / `does not contain`: the value is text to look for inside the stored one. */
export const isTextMatchOperator = comparison => comparison === 'contains' || comparison === 'ncontains';

/**
 * Words for the relative literals the grammar speaks, so a chip never shows
 * `-30d`. The menu of presets that PRODUCE them lives in `utils/datePresets.js`
 * (spans, Between…, Overdue and the rest); this map is only the reverse
 * reading, which every surface that renders a value needs — including a query
 * somebody hand-typed in the Advanced tab.
 */
export const RELATIVE_VALUE_LABELS = {
	now: 'Today',
	today: 'Today',
	'-7d': 'Last 7 days',
	'-30d': 'Last 30 days',
	'-3mo': 'Last 3 months',
	'-1y': 'Last year',
};

const OFFSET_UNIT_WORDS = { m: 'minute', h: 'hour', d: 'day', w: 'week', mo: 'month', y: 'year' };
// Both spellings the grammar keeps: the bare offset (`-7d`, anchored at today)
// and the explicit anchor (`today+7d`, `now-2h`).
const OFFSET_RE = /^(?:(today|now))?([+-])(\d+)(mo|[dwyhm])$/;

/** A relative literal in words: `-2w` → "2 weeks ago", `+7d` → "in 7 days". */
export function relativeLabel(value) {
	const text = String(value ?? '').toLowerCase();
	if (RELATIVE_VALUE_LABELS[text]) return RELATIVE_VALUE_LABELS[text];
	const match = OFFSET_RE.exec(text);
	if (!match) return text;
	const [, , sign, amount, unit] = match;
	const word = OFFSET_UNIT_WORDS[unit];
	const plural = Number(amount) === 1 ? word : `${word}s`;
	return sign === '+' ? `in ${amount} ${plural}` : `${amount} ${plural} ago`;
}

/**
 * Stable identity for a field/path across renders and selects.
 *
 * Three roots can spell the same thing and mean three different questions: the
 * ELEMENT `people` (quoted when it collides with a system field), the system
 * field itself, and the FACET `@people`. The key is what keeps them apart, so
 * every caller that has a condition or a row passes all three coordinates.
 */
export function pathKey(path, forceElement = false, facet = false) {
	const joined = (path || []).join('.');
	if (facet) return `${FACET_PREFIX}${joined}`;
	return forceElement ? `"${joined}` : joined;
}

/**
 * Record one card's definition of a field's name under its element id: the
 * cards that hold that id, and the definition's own label, type and options.
 * An element with no id (one the server has not given one yet) adds none.
 */
function addDefinition(field, element, cardId, cardLabel) {
	const elementId = typeof element?.id === 'string' && ELEMENT_ID_RE.test(element.id) ? element.id : null;
	if (!elementId) return;
	const known = field.definitions.find(def => def.id === elementId);
	if (known) {
		known.cardIds.push(cardId);
		known.cardLabels.push(cardLabel);
		return;
	}
	field.definitions.push({
		id: elementId,
		label: element?.label?.singular || element.name,
		dataType: element?.dataType || 'text-small',
		options: element?.options || {},
		fromSet: element?.fromSet || null,
		// The ids a copy was copied from (an element set's "Copy into this
		// card", the archive's clones): `derivedFrom` its lineage's root,
		// `derivedVia` its immediate source when that is another copy — a
		// stored `#<either id>` names this definition where nothing here
		// carries that id itself, as the API reads it (keepr-api
		// utils/kql/compile.js indexElementIds).
		derivedFrom: typeof element?.derivedFrom === 'string' && ELEMENT_ID_RE.test(element.derivedFrom) ? element.derivedFrom : null,
		derivedVia: typeof element?.derivedVia === 'string' && ELEMENT_ID_RE.test(element.derivedVia) ? element.derivedVia : null,
		cardIds: [cardId],
		cardLabels: [cardLabel],
	});
}

// How many lookups deep a hop's leaf is looked for: the API's own bound on a
// dot-walk (keepr-api SCHEMA § KQL, three hops).
const HOP_DEPTH = 3;

/**
 * The definitions a HOP's later segments may name by id (`#baby.#name`,
 * review EK-W1): the elements of every card a lookup of these cards points
 * at — the target and the cards under it, as the API resolves the leaf where
 * the hop enters — and of the cards THEIR lookups point at, to the API's
 * depth. One flat list of definitions (addDefinition's shape), each with its
 * cards; empty when the page passes no `getElementsForCard`. A page whose
 * fields hold one card's family (the chart builder, a lookup's "Offer only
 * items where…") still reads the leaf it walks into.
 */
function hopDefinitionsFor(rootElements, { cards = [], getElementsForCard }) {
	if (typeof getElementsForCard !== 'function') return [];
	const cardById = new Map();
	const childrenOf = new Map();
	(cards || []).forEach((card) => {
		const id = normalizeId(card?._id);
		if (!id) return;
		cardById.set(id, card);
		const parent = normalizeId(card?.parentCardId);
		if (parent) childrenOf.set(parent, [...(childrenOf.get(parent) || []), id]);
	});
	const familyOf = (root) => {
		const out = [root];
		for (let i = 0; i < out.length && out.length < 500; i += 1) (childrenOf.get(out[i]) || []).forEach(child => { if (!out.includes(child)) out.push(child); });
		return out;
	};
	const holder = { definitions: [] };
	const visited = new Set();
	let lookups = rootElements.filter(element => element?.dataType === 'card-lookup');
	for (let depth = 0; depth < HOP_DEPTH && lookups.length; depth += 1) {
		const next = [];
		lookups.forEach((lookup) => {
			const target = normalizeId(lookup?.options?.lookupCardId);
			if (!target) return;
			familyOf(target).forEach((cardId) => {
				if (visited.has(cardId)) return;
				visited.add(cardId);
				const card = cardById.get(cardId);
				const cardLabel = card?.label?.singular || card?.name || 'Card';
				(getElementsForCard(cardId) || []).forEach((element) => {
					if (!element?.name) return;
					addDefinition(holder, element, cardId, cardLabel);
					if (element.dataType === 'card-lookup') next.push(element);
				});
			});
		});
		lookups = next;
	}
	return holder.definitions;
}

/**
 * The filterable vocabulary of a collection: the union of elements across the
 * member cards the caller can read, plus the system fields.
 *
 * `cardIds` is the same restriction the card picker applies — null means
 * unrestricted, an (even empty) Set restricts. Elements declared by more than
 * one card collapse to one option, keeping the FIRST definition's dataType, the
 * way the API compiler resolves a name against the collection's vocabulary.
 *
 * FACETS LEAD (`utils/facets.js`): one `@<card-key>` field per target card
 * that two or more differently named lookups point at, so "which person?" is
 * one row rather than three OR-ed ones. `facetCardKeys` is the collection's
 * own list (`collection.facets`), which promotes a card on a single element.
 * The cards and collections lists pass neither `getElementsForCard` nor a
 * collection, and so offer no facets: there is no `@` root on those subjects.
 *
 * `noGroups` (KPR-893): a who-sees-what text may not ask about a repeating
 * group's entries, their count or whether it is empty (keepr-api answers 400
 * `repeating_group_in_access` since KPR-781), so the access editors leave the
 * groups, and the entry counts beside them, out of the vocabulary.
 */
export function buildFilterFields({ cards = [], cardIds = null, getElementsForCard, facetCardKeys = null, elementSets = null, hopTargets = null, family = null, keepKeys = false, noGroups = false } = {}) {
	const byName = new Map();
	let cardCount = 0;
	// Every element of the page's cards: the lookups among them are where a
	// hop's leaf is looked for (hopDefinitionsFor).
	const rootElements = [];

	(cards || []).forEach(card => {
		const id = normalizeId(card?._id);
		if (!id) return;
		if (cardIds && !cardIds.has(id)) return;
		cardCount++;
		const cardLabel = card?.label?.singular || card?.name || 'Card';
		const elements = (typeof getElementsForCard === 'function' ? getElementsForCard(id) : card?.elements) || [];
		elements.forEach(element => {
			const name = element?.name;
			if (!name) return;
			rootElements.push(element);
			const existing = byName.get(name);
			if (existing) {
				existing.cardLabels.push(cardLabel);
				// Which cards DECLARE the element, by id. Labels are display
				// strings and two cards may share one ("Note" and "Note"), so
				// anything deciding membership — the chip row's scope, above
				// all — has to key on ids.
				existing.cardIds.push(id);
				addDefinition(existing, element, id, cardLabel);
				return;
			}
			const field = {
				key: pathKey([name], SYSTEM_FIELD_SET.has(name)),
				path: [name],
				// An element literally named `card`/`set`/`created`/… only
				// means the element when its first segment is quoted.
				forceElement: SYSTEM_FIELD_SET.has(name),
				label: element?.label?.singular || name,
				dataType: element?.dataType || 'text-small',
				options: element?.options || {},
				cardLabels: [cardLabel],
				cardIds: [id],
				system: false,
				// The element set that supplied it (the resolver's `fromSet`
				// stamp), null for a card's own element.
				fromSet: element?.fromSet || null,
				// Each DEFINITION of the name, by its element id (KPR-168): a
				// query names one as `#<id>`, and `findField` opens it as that
				// definition — its card, its type — never the first card's.
				definitions: [],
			};
			addDefinition(field, element, id, cardLabel);
			byName.set(name, field);
		});
	});

	// The definitions a hop's later segments name by id, held by every element
	// field (one list, shared): a filter that walks a lookup keeps its words
	// wherever the page's fields leave the target's card out.
	const hopDefinitions = hopDefinitionsFor(rootElements, { cards, getElementsForCard });
	const elementFields = [...byName.values()]
		.filter(field => !(noGroups && isEntriesField(field)))
		.map(field => ({
			...field,
			hopDefinitions,
			// A set's element that several cards carry is one field across
			// them — "Phone · Contact" says which Phone, and that it is the
			// shared one, where a card's own "Phone" would not.
			label: field.fromSet?.name && field.cardLabels.length > 1
				? `${field.label} · ${field.fromSet.name}`
				: field.label,
			// "only on some cards" is worth saying out loud: a condition on it
			// silently excludes every item of the cards that lack it.
			partial: field.cardLabels.length < cardCount,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	// `keepKeys`: text the API itself resolves — who-sees-what text (read
	// strictly: a key two tiers share is refused, never picked), a lookup's
	// picker filter (resolved in the target's home), a notification's
	// trigger (read where it fires) — keeps the card, set and facet keys it
	// is written with on Apply; they still READ as their records.
	const facetFields = buildFacetFields({ cards, cardIds, getElementsForCard, facetCardKeys, keepKeys });
	const setField = elementSetFilterField(elementSets, { keepKeys });
	const cardField = cardFilterField(cards, { cardIds, keepKeys });
	const collectionField = collectionFamilyFilterField(family);
	const hopFields = buildHopFields(elementFields, { getElementsForCard, hopTargets });

	const systemFields = SYSTEM_FILTER_FIELDS.map(field => ({ ...field, cardLabels: [], cardIds: [], partial: false }));
	return [
		...facetFields,
		...elementFields,
		// A repeating group's entry count, beside the group (KPR-683).
		...elementFields.filter(isEntriesField).map(entryCountField),
		...hopFields,
		...systemFields,
		{ ...CREATOR_FILTER_FIELD, forceElement: false, cardLabels: [], cardIds: [], partial: false },
		...datePartFilterFields([...systemFields, ...elementFields]),
		...(setField ? [{ ...setField, cardLabels: [], cardIds: [], partial: false }] : []),
		...(cardField ? [{ ...cardField, cardLabels: [], cardIds: [], partial: false }] : []),
		...(collectionField ? [{ ...collectionField, cardLabels: [], cardIds: [], partial: false }] : []),
	];
}

/**
 * THE CROSS-COLLECTION HOP (keepr-api PR L5, § KQL "A hop may leave the
 * collection"): a lookup whose target card lives in ANOTHER collection — one a
 * collection link lends this one — may be walked into: `visit.status = open`
 * asks about the visit's own record, in its own collection, through the
 * reader's own access there. One field per element of the target card (its
 * chain and sets, as `getElementsForCard` merges them), keyed on the dotted
 * path, labelled "Visit › Status" and carrying `hop: { collectionId, name }`
 * so the menu can say "from Clinic".
 *
 * `hopTargets` is `Map(targetCardId → { collectionId, name })` — the cards the
 * page knows to live elsewhere. A lookup into one of this collection's own
 * cards is not a hop here: those are asked as the lookup itself.
 *
 * Stored queries do not cross (a filtered grant, a link query, a dynamic
 * group, a share link): the API answers a hop there as "cannot answer" and
 * warns, or — a share link — refuses. `queryCrossesCollections` finds them.
 */
export function buildHopFields(elementFields = [], { getElementsForCard, hopTargets = null } = {}) {
	if (!(hopTargets instanceof Map) || !hopTargets.size || typeof getElementsForCard !== 'function') return [];
	const out = [];
	const seen = new Set();
	(elementFields || []).forEach(field => {
		if (field?.system || field?.facet || field?.dataType !== 'card-lookup' || (field.path || []).length !== 1) return;
		const targetId = normalizeId(field.options?.lookupCardId);
		const target = targetId ? hopTargets.get(targetId) : null;
		if (!target) return;
		(getElementsForCard(targetId) || []).forEach(element => {
			const name = element?.name;
			if (!name) return;
			const path = [field.path[0], name];
			const key = pathKey(path, field.forceElement);
			if (seen.has(key)) return;
			seen.add(key);
			out.push({
				key,
				path,
				forceElement: field.forceElement,
				label: `${field.label} › ${element?.label?.singular || name}`,
				dataType: element?.dataType || 'text-small',
				// Never a one-click chip: the chip row is this collection's usual
				// questions, and a hop is asked on purpose, from the builder.
				options: { ...(element?.options || {}), quickFilter: false },
				cardLabels: field.cardLabels,
				cardIds: field.cardIds,
				system: false,
				partial: field.partial,
				fromSet: null,
				hop: { collectionId: target.collectionId, name: target.name, lookupCardId: targetId },
			});
		});
	});
	return out.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Does this query walk a lookup into another collection's card? `fields` is
 * the page's vocabulary (with its hop fields); the answer is the paths of the
 * conditions that do. A query that does not parse crosses nothing.
 */
export function queryCrossesCollections(text, fields = []) {
	const trimmed = (text || '').trim();
	if (!trimmed) return [];
	const hopKeys = new Set((fields || []).filter(field => field?.hop).map(field => field.key));
	if (!hopKeys.size) return [];
	let ast;
	try {
		ast = parse(trimmed);
	} catch (err) {
		return [];
	}
	const out = [];
	const walk = node => {
		if (!node) return;
		if (node.op === 'cmp') {
			const key = pathKey(node.path || [], !!node.forceElement, !!node.facet);
			if (hopKeys.has(key)) out.push((node.path || []).join('.'));
			return;
		}
		(node.args || []).forEach(walk);
		if (node.arg) walk(node.arg);
	};
	walk(ast);
	return out;
}

/**
 * The query with every facet named by an ALIAS — its card's key, as text
 * written before F3 named it — renamed to the facet's own path, the card's
 * id: `@people = x` → `@<id> = x` (deterministic filters, ruling E11).
 * `{ ast, changed }`; a facet no field knows by that alias stays as written.
 */
export function normalizeFacetAliases(ast, fields) {
	if (!ast) return { ast, changed: false };
	let changed = false;
	const walk = (node) => {
		if (!node || typeof node !== 'object') return node;
		if (Array.isArray(node.args)) return { ...node, args: node.args.map(walk) };
		if (node.arg) return { ...node, arg: walk(node.arg) };
		if (!node.facet || !Array.isArray(node.path)) return node;
		const key = pathKey(node.path, false, true);
		// A facet that already IS that spelling is no alias of another.
		if ((fields || []).some(candidate => candidate.key === key)) return node;
		const named = (fields || []).filter(candidate => Array.isArray(candidate.aliases) && candidate.aliases.includes(key));
		// A vocabulary that keeps its keys (`keepKeys`) reads the alias and
		// leaves the text alone.
		if (named.some(candidate => candidate.keepKeys)) return node;
		// Exactly one: two cards sharing the key (a collection's own and a
		// global one) is a question for the person, never a guess — the text
		// stays as written and the server reads it as it always has.
		if (named.length !== 1) return node;
		changed = true;
		return { ...node, path: [...named[0].path] };
	};
	const next = walk(ast);
	return { ast: changed ? next : ast, changed };
}

/** `normalizeFacetAliases` over TEXT: canonical text when a facet was renamed, else the text as given. */
export function normalizeFacetAliasText(text, fields) {
	const trimmed = (text || '').trim();
	if (!trimmed) return text;
	let ast;
	try { ast = parse(trimmed); } catch (err) { return text; }
	const { ast: next, changed } = normalizeFacetAliases(ast, fields);
	return changed ? serialize(next) : text;
}

/** Find the descriptor for a path, or synthesize one so an unknown field still renders. */
export function findField(fields, path, forceElement = false, facet = false) {
	const key = pathKey(path, forceElement, facet);
	// `aliases`: the other spellings of the same field — a facet named by its
	// card's KEY (`@people`), written before filters named cards by id (F3).
	const found = (fields || []).find(field => field.key === key)
		|| (fields || []).find(field => Array.isArray(field.aliases) && field.aliases.includes(key));
	if (found) return found;
	// A facet somebody typed in the Advanced tab (or a link from a collection
	// whose cards this one does not hold) is still a FACET: it must offer the
	// membership operators and the item picker, not a text box, and its chip
	// must resolve ids to titles like any other lookup.
	if (facet) {
		const cardKey = (path || []).join('.');
		// A facet by a card ID the vocabulary does not hold says so rather
		// than printing the id (deterministic filters F3).
		const named = /^[0-9a-f]{24}$/i.test(cardKey) ? 'A card not in this collection' : cardKey;
		return {
			key: facetFieldKey(cardKey),
			path: [...(path || [])],
			forceElement: false,
			facet: true,
			kind: FACET_KIND,
			cardKey,
			label: `${named} (any lookup)`,
			shortLabel: named,
			dataType: 'card-lookup',
			options: {},
			lookupCardId: null,
			targetCardIds: [],
			memberElements: [],
			cardLabels: [],
			cardIds: [],
			partial: false,
			system: false,
			unknown: true,
		};
	}
	// A repeating group's ENTRY COUNT (`#id.count`, `ingredient.count`, KPR-683):
	// the number of entries, when the root is a group of the vocabulary. On
	// anything else the same path is a dot-walk, read below as it always was.
	if (!facet && Array.isArray(path) && path.length === 2 && path[1] === ENTRY_COUNT) {
		const group = entryGroupFieldOf(fields, path[0], forceElement);
		if (group) return { ...entryCountField(group), key, path: [...path], forceElement };
	}
	// An ELEMENT GROUP (element ids step 3, "Any of these cards"): a row's
	// one-segment stand-in for the comparison it writes once per element.
	if (!facet && Array.isArray(path) && path.length === 1 && isElementGroupSegment(path[0])) return elementGroupField(fields, path[0]);
	// An element named by its ID (`#k7f3q2xa`, KPR-168): the one definition
	// that carries it — a chart's door, a filter saved with ids.
	if (!facet && Array.isArray(path) && path.some(isElementIdSegment)) return elementIdField(fields, path);
	// A date part or "Created by" named where the page built none (a link
	// from a chart, opened on a surface with a narrower vocabulary): the
	// field itself, so its row gets the picker and its chip reads in words.
	if (!facet && Array.isArray(path) && path.length === 2) {
		if (!forceElement && path[0] === 'created' && path[1] === CREATOR_PART) {
			return { ...CREATOR_FILTER_FIELD, forceElement: false, cardLabels: [], cardIds: [], partial: false };
		}
		if (DATE_PARTS.includes(path[1])) {
			const rootKey = pathKey([path[0]], forceElement);
			const root = (fields || []).find(field => field.key === rootKey && !field.facet)
				|| (!forceElement ? SYSTEM_FILTER_FIELDS.find(field => field.key === path[0]) : null);
			if (root && (PARTS_OF_TYPE[root.dataType] || []).includes(path[1])) return datePartField(root, path[1]);
		}
	}
	// `tags` named where the page built no tags field (a subject without
	// one, an older link): the field itself, with an empty vocabulary — its
	// row still gets the picker and its chip still reads the words it can.
	if (!forceElement && Array.isArray(path) && path.length === 1 && path[0] === TAG_FIELD_KEY) {
		return tagsFilterField();
	}
	// Dot-walks and names outside this collection's vocabulary (a shared link
	// from another collection, say) get a read-only-ish stand-in rather than
	// blowing up the row.
	return {
		key,
		path: [...(path || [])],
		forceElement,
		label: (path || []).join('.'),
		dataType: 'text-small',
		options: {},
		cardLabels: [],
		cardIds: [],
		partial: false,
		system: false,
		unknown: true,
	};
}

// ------------------------------------------------------------ element ids

/** What a query says for an element id the page's vocabulary does not hold. */
export const GONE_ELEMENT_LABEL = 'An element that is no longer here';

const idIndexes = new WeakMap();

/**
 * Element id → `{ field, def }` over a field list: every definition the
 * element fields recorded (`definitions`, buildFilterFields), memoised per
 * list. Ids are unique across the platform, so one index serves every
 * segment of a path, a hop's leaf included.
 */
function elementIdIndex(fields) {
	const list = Array.isArray(fields) ? fields : [];
	if (idIndexes.has(list)) return idIndexes.get(list);
	const index = indexDefinitions(list.flatMap(field => (field?.definitions || []).map(def => ({ field, def }))));
	idIndexes.set(list, index);
	return index;
}

/**
 * id → `{ field, def }` over `hits`: each definition under its own id, then —
 * where nothing here carries an id — under the ids it was copied from
 * (`derivedVia`, `derivedFrom`), as the API reads a stored `#id` after "Copy
 * into this card" or the archive.
 */
function indexDefinitions(hits) {
	const index = new Map();
	hits.forEach((hit) => { if (hit.def?.id && !index.has(hit.def.id)) index.set(hit.def.id, hit); });
	hits.forEach((hit) => {
		[hit.def?.derivedVia, hit.def?.derivedFrom].forEach((from) => { if (from && !index.has(from)) index.set(from, hit); });
	});
	return index;
}

const hopIndexes = new WeakMap();

/** Element id → `{ field, def }` over the definitions a hop walks into (hopDefinitionsFor), memoised per list. */
function hopIdIndex(fields) {
	const list = Array.isArray(fields) ? fields : [];
	if (hopIndexes.has(list)) return hopIndexes.get(list);
	const seen = new Set();
	const hits = [];
	list.forEach((field) => {
		const defs = field?.hopDefinitions;
		if (!Array.isArray(defs) || seen.has(defs)) return;
		seen.add(defs);
		defs.forEach(def => hits.push({ field: { definitions: [def], fromSet: null, partial: false, cardIds: def.cardIds }, def }));
	});
	const index = indexDefinitions(hits);
	hopIndexes.set(list, index);
	return index;
}

/**
 * The hit for segment `i` of a path naming elements by id: the root among the
 * page's own definitions; a later segment there too, else among the cards a
 * lookup walks into (review EK-W1). null for an id nothing here holds,
 * undefined for a name.
 */
function elementIdHit(fields, path, i) {
	const segment = path[i];
	if (!isElementIdSegment(segment)) return undefined;
	const id = elementIdOf(segment);
	return elementIdIndex(fields).get(id) || (i > 0 ? hopIdIndex(fields).get(id) : null) || null;
}

/**
 * The words for segment `i` of `path` when it names an element by id — its
 * definition's label (a later segment: the element the hop walks into) — or
 * null when nothing here holds the id. The Advanced tab's line of ids
 * (utils/filterIds.js) reads each segment through this.
 */
export function elementIdSegmentLabel(fields, path, i) {
	if (!Array.isArray(path)) return null;
	const hit = elementIdHit(fields, path, i);
	return hit ? definitionLabel(hit) : null;
}

/**
 * The pathKeys a field's definitions are named by when a query names them by
 * id (`#k7f3q2xa`) — the other spellings of an element field of one type, so
 * a quick-filter chip reads and clears a condition a chart's door wrote by id
 * (review EK-W2).
 */
export function elementIdKeys(field) {
	// Only an element field carries `definitions` (buildFilterFields): a
	// system field, a facet, a hop or a date part has none.
	return ((field && field.definitions) || [])
		.filter(def => def?.id && def.dataType === field.dataType)
		.map(def => pathKey([`#${def.id}`]));
}

/**
 * The words for one definition: its label, and — where another definition of
 * the same name is in the vocabulary — the card that holds it ("Volume ·
 * Breast Milk Expression"), as a shared set's element says its set.
 */
function definitionLabel({ field, def }) {
	if (field.definitions.length < 2) return field.fromSet?.name && def.cardLabels.length > 1 ? `${def.label} · ${field.fromSet.name}` : def.label;
	return `${def.label} · ${[...new Set(def.cardLabels)].join(', ')}`;
}

/**
 * The field a path naming elements by ID opens as (findField). One segment:
 * that definition — its label, type, options and cards — keyed on the path,
 * so a row edits the id it was opened with and the text keeps it. A date
 * part on one: that element's part. A hop (`#baby.#name`): each segment in
 * words, the leaf's type. An id the vocabulary does not hold reads as an
 * element that is no longer here — never as its raw id, never as another
 * element.
 */
function elementIdField(fields, path) {
	const hits = path.map((segment, i) => elementIdHit(fields, path, i));
	const root = hits[0];
	const fromDef = hit => ({
		key: pathKey([path[0]]),
		path: [path[0]],
		forceElement: false,
		label: definitionLabel(hit),
		dataType: hit.def.dataType,
		options: hit.def.options,
		cardLabels: hit.def.cardLabels,
		cardIds: hit.def.cardIds,
		// Only on some cards: wherever the name is, or where another card's
		// definition of it holds the rest.
		partial: !!hit.field.partial || hit.def.cardIds.length < (hit.field.cardIds || []).length,
		system: false,
		fromSet: hit.def.fromSet,
		elementId: hit.def.id,
	});
	if (path.length === 1) {
		if (root) return fromDef(root);
		return { key: pathKey(path), path: [...path], forceElement: false, label: GONE_ELEMENT_LABEL, dataType: 'text-small', options: {}, cardLabels: [], cardIds: [], partial: false, system: false, unknown: true, goneElement: true };
	}
	if (path.length === 2 && DATE_PARTS.includes(path[1]) && root && (PARTS_OF_TYPE[root.def.dataType] || []).includes(path[1])) {
		return datePartField(fromDef(root), path[1]);
	}
	// A hop: every segment in words — a later one as the element the hop
	// walks into, wherever the page's own fields leave its card out.
	const gone = hits.some(hit => hit === null);
	const words = path.map((segment, i) => (hits[i] ? hits[i].def.label : (hits[i] === null ? GONE_ELEMENT_LABEL.toLowerCase() : segment)));
	const leaf = hits[hits.length - 1];
	return {
		key: pathKey(path),
		path: [...path],
		forceElement: false,
		label: words.join(' › '),
		dataType: leaf ? leaf.def.dataType : 'text-small',
		options: { ...(leaf ? leaf.def.options : {}), quickFilter: false },
		cardLabels: root ? root.def.cardLabels : [],
		cardIds: root ? root.def.cardIds : [],
		partial: false,
		system: false,
		...(gone || !leaf ? { unknown: true } : {}),
		...(gone ? { goneElement: true } : {}),
	};
}

// ---------------------------------------------------------- element groups

/**
 * AN ELEMENT GROUP (element ids step 3, KPR-238 P4; keepr-api docs/SCHEMA.md
 * "Element names, written as ids"): one condition asked of several elements
 * that share a name on different cards — "Any of these cards" — written as
 * the comparison once per element, joined so each card's items are read by
 * their own: `(#a = x or #b = x)`, or `#a != x and #b != x` where a missing
 * value matches (`!=`, `not in`, `!~`, `is empty`). Plain KQL; the server
 * writes the same text for a refusal answered with every candidate.
 *
 * A builder ROW holds a group as ONE segment, `#a|#b` (the ids, sorted): its
 * field (`findField` → `elementGroupField`) reads in words, its select keeps
 * one value, and `rowNodes` writes the group. That segment is never KQL — it
 * exists only between `astToRows` and `rowNodes`.
 */
const ELEMENT_GROUP_SEGMENT_RE = /^#[a-z0-9]{8}(\|#[a-z0-9]{8})+$/;
const NEGATIVE_CMPS = new Set(['ne', 'nin', 'ncontains']);

/** True for a row's group segment (`#a|#b`). */
export const isElementGroupSegment = segment => typeof segment === 'string' && ELEMENT_GROUP_SEGMENT_RE.test(segment);

/** The group segment for these ids (deduplicated, sorted) — or the one id's own segment. */
export function elementGroupSegment(ids) {
	const list = [...new Set((ids || []).map(id => String(id).replace(/^#/, '')).filter(id => ELEMENT_ID_RE.test(id)))].sort();
	return list.map(id => `#${id}`).join('|');
}

/** The ids a group segment names. */
export const elementGroupIds = segment => (isElementGroupSegment(segment) ? segment.split('|').map(part => part.slice(1)) : []);

/** Does a missing value match this comparison? Then a group's branches join with `and`, else `or`. */
export const missingMatches = comparison => comparison === 'empty' || NEGATIVE_CMPS.has(comparison);

/** The group's KQL: the comparison once per element. */
export function elementGroupNode(ids, comparison, value) {
	const args = ids.map(id => makeCmp([`#${id}`], comparison, value));
	return { op: missingMatches(comparison) ? 'and' : 'or', args };
}

/** A one-segment comparison on an element named by id (the shape a group's branch has). */
// (Never a repeating group's block: `#a has (…) or #b has (…)` names the columns of
// two groups, which no one value control can ask — it is the Advanced tab's.)
const isIdBranch = node => !!node && node.op === 'cmp' && !node.facet && !node.forceElement
	&& Array.isArray(node.path) && node.path.length === 1 && isElementIdSegment(node.path[0])
	&& node.cmp !== BLOCK_CMP && !isBlock(node);

const sameComparison = (a, b) => a.cmp === b.cmp && JSON.stringify(a.value ?? null) === JSON.stringify(b.value ?? null);

/**
 * `node` read as ONE element group — `{ ids, cmp, value }` — or null: an
 * `or` (an `and` where a missing value matches) of two or more one-segment
 * comparisons on different elements named by id, alike in everything else.
 */
export function readElementGroup(node) {
	if (!node || (node.op !== 'or' && node.op !== 'and') || !Array.isArray(node.args) || node.args.length < 2) return null;
	const [first] = node.args;
	if (!node.args.every(isIdBranch) || !node.args.every(arg => sameComparison(arg, first))) return null;
	if ((node.op === 'and') !== missingMatches(first.cmp)) return null;
	const ids = node.args.map(arg => elementIdOf(arg.path[0]));
	if (new Set(ids).size !== ids.length) return null;
	return { ids, cmp: first.cmp, value: first.value };
}

/**
 * The top-level conditions of an `and` with each group read as one: a
 * positive group is a nested `or`; the parser flattens a negative one into
 * its siblings, so siblings alike in all but their element id — where a
 * missing value matches — are one group again, at the first one's place.
 * Entries are `{ node }` or `{ group: { ids, cmp, value } }`.
 */
function groupedConditions(nodes) {
	const out = [];
	const pending = new Map();
	nodes.forEach((node) => {
		const group = readElementGroup(node);
		if (group) { out.push({ group }); return; }
		if (isIdBranch(node) && missingMatches(node.cmp)) {
			const key = JSON.stringify([node.cmp, node.value ?? null]);
			const entry = pending.get(key);
			if (entry && !entry.ids.includes(elementIdOf(node.path[0]))) {
				entry.ids.push(elementIdOf(node.path[0]));
				return;
			}
			if (!entry) {
				const fresh = { ids: [elementIdOf(node.path[0])], cmp: node.cmp, value: node.value, node };
				pending.set(key, fresh);
				out.push({ pendingGroup: fresh });
				return;
			}
		}
		out.push({ node });
	});
	return out.map(entry => (entry.pendingGroup
		? (entry.pendingGroup.ids.length > 1
			? { group: { ids: entry.pendingGroup.ids, cmp: entry.pendingGroup.cmp, value: entry.pendingGroup.value } }
			: { node: entry.pendingGroup.node })
		: entry));
}

/** A group's stand-in: ONE comparison on its segment (`#a|#b`), as a row holds it. */
const groupStandIn = group => makeCmp([elementGroupSegment(group.ids)], group.cmp, group.value);

/**
 * The query's top-level conditions as the rows and the chips read them: each
 * element group as ONE comparison on its segment. Write a list of these back
 * through `expandElementGroups`.
 */
export function topLevelConditionNodes(ast) {
	if (!ast) return [];
	const whole = readElementGroup(ast);
	if (whole) return [groupStandIn(whole)];
	const nodes = ast.op === 'and' && Array.isArray(ast.args) ? ast.args : [ast];
	return groupedConditions(nodes).map(entry => (entry.group ? groupStandIn(entry.group) : entry.node));
}

/** A node with a group's stand-in written out as the group (any other node as it is). */
export function expandElementGroups(node) {
	if (node && node.op === 'cmp' && Array.isArray(node.path) && node.path.length === 1 && isElementGroupSegment(node.path[0])) {
		return elementGroupNode(elementGroupIds(node.path[0]), node.cmp, node.value);
	}
	return node;
}

/**
 * The field a group segment opens as: "Status · any of these cards" where
 * every id is a definition of one element field, else the definitions'
 * words joined ("Status · Task or State · Bug"); the first definition's type,
 * and every definition's choices (one list, by value) so a choice row offers
 * what any of the cards stores.
 */
function elementGroupField(fields, segment) {
	const ids = elementGroupIds(segment);
	const hits = ids.map(id => elementIdIndex(fields).get(id) || null);
	const known = hits.filter(Boolean);
	const first = known[0] || null;
	const oneField = !!first && known.length === hits.length && known.every(hit => hit.field === first.field);
	const label = !first
		? GONE_ELEMENT_LABEL
		: (oneField ? `${first.field.label} · ${ANY_OF_THESE_CARDS_WORDS}` : known.map(definitionLabel).join(' or '));
	const choices = [];
	const seen = new Set();
	known.forEach((hit) => {
		// A Choice definition's list only: a list another type carries is inert.
		choiceListOf(hit.def).forEach((choice) => {
			const value = choice !== null && typeof choice === 'object' ? choice.value : choice;
			const key = String(value);
			if (seen.has(key)) return;
			seen.add(key);
			choices.push(choice);
		});
	});
	const options = first ? { ...first.def.options, ...(choices.length ? { choices } : {}), quickFilter: false } : {};
	return {
		key: pathKey([segment]),
		path: [segment],
		forceElement: false,
		label,
		dataType: first ? first.def.dataType : 'text-small',
		options,
		cardLabels: [...new Set(known.flatMap(hit => hit.def.cardLabels))],
		cardIds: [...new Set(known.flatMap(hit => hit.def.cardIds))],
		partial: false,
		system: false,
		elementGroup: ids,
		...(known.length === hits.length ? {} : { unknown: true, goneElement: true }),
	};
}

/** What "Any of these cards" reads as after an element's label. */
export const ANY_OF_THESE_CARDS_WORDS = 'any of these cards';

/** Two definitions a group may join: alike enough that one value control asks both. */
// (Never a repeating group: its conditions name the columns of ONE definition,
// which another card's copy does not share.)
const groupable = defs => defs.length > 1
	&& fieldKindOf(defs[0]) !== ENTRIES_KIND
	&& defs.every(def => fieldKindOf(def) === fieldKindOf(defs[0]));

/**
 * A field's definitions with each COPY folded into the element it was
 * copied from (`derivedFrom`, "Copy into this card", an archive clone): one
 * lineage is one element to the server — `#<root>` reads every copy (keepr-api
 * compile.js indexElementIds) — so the menu offers it once, with every card
 * that holds it (the P4 review's R-KB1). A copy whose root is not here stays
 * an element of its own.
 */
function foldLineages(defs) {
	const byId = new Map(defs.map(def => [def.id, def]));
	const folded = new Map();
	defs.forEach((def) => {
		const rootId = [def.derivedFrom, def.derivedVia].find(id => id && id !== def.id && byId.has(id)) || def.id;
		const root = byId.get(rootId);
		const into = folded.get(rootId) || { ...root, cardIds: [...root.cardIds], cardLabels: [...root.cardLabels] };
		if (def !== root) {
			def.cardIds.forEach((cardId, i) => {
				if (into.cardIds.includes(cardId)) return;
				into.cardIds.push(cardId);
				into.cardLabels.push(def.cardLabels[i]);
			});
		}
		folded.set(rootId, into);
	});
	return [...folded.values()];
}

/**
 * WHAT THE FIELD MENU OFFERS for one field (element ids step 3, KPR-238 P4:
 * "When the person picks an element in a builder, write its #id"). Each
 * entry is `{ key, field, label }` — `key` the option's value and the row's
 * pathKey, `field` what the row then edits:
 *
 *   an element one definition answers to   its id (`#k7f3q2xa`), labelled
 *                                          as the field is
 *   one several definitions answer to      each definition by its id,
 *                                          labelled with its card ("Status ·
 *                                          Task"), then — where one value
 *                                          control can ask them all — "Status
 *                                          · any of these cards", the group
 *   anything else, or `keepNames`          the field itself, by its key
 *
 * `keepNames`: text the server resolves in a vocabulary the page cannot see
 * whole — who-sees-what text, read in every sub-collection too (⚑ K4) —
 * keeps writing the name, and the server asks at save. A name the server
 * reads in the page's own vocabulary is written as the id the person picked.
 */
export function elementFieldEntries(field, fields, { keepNames = false } = {}) {
	const plain = [{ key: field.key, field, label: field.label }];
	// A group's entry count: the group's id and `.count`, once per definition.
	if (isEntryCountField(field) && !keepNames && Array.isArray(field.definitions) && field.definitions.length) {
		const defs = foldLineages(field.definitions);
		return defs.map((def) => {
			const byId = findField(fields, [`#${def.id}`, ENTRY_COUNT]);
			const label = defs.length === 1 ? field.label : `${def.label} · number of entries · ${[...new Set(def.cardLabels)].join(', ')}`;
			return { key: byId.key, field: { ...byId, label, cardIds: def.cardIds, cardLabels: def.cardLabels }, label };
		});
	}
	if (keepNames || !field || field.system || field.facet || field.hop || field.notOffered || isDatePartField(field)) return plain;
	if (!Array.isArray(field.path) || field.path.length !== 1) return plain;
	const defs = foldLineages(Array.isArray(field.definitions) ? field.definitions : []);
	if (!defs.length) return plain;
	// The element by its id, saying every card that holds it — its copies' too.
	const entryFor = (def, label) => {
		const byId = findField(fields, [`#${def.id}`]);
		return { key: byId.key, field: { ...byId, label, cardIds: def.cardIds, cardLabels: def.cardLabels }, label };
	};
	if (defs.length === 1) return [{ ...entryFor(defs[0], field.label), field: { ...entryFor(defs[0], field.label).field, partial: field.partial } }];
	const entries = defs.map(def => entryFor(def, `${def.label} · ${[...new Set(def.cardLabels)].join(', ')}`));
	if (groupable(defs)) {
		const segment = elementGroupSegment(defs.map(def => def.id));
		const group = findField(fields, [segment]);
		entries.push({ key: group.key, field: group, label: group.label });
	}
	return entries;
}

// ------------------------------------------------------------------- values

const looksNumeric = raw => raw !== '' && raw !== null && raw !== undefined && Number.isFinite(Number(raw));

// A number in plain digits, never "1e-7", which the API's parser does not read.
const plainNumber = (n) => {
	const text = String(n);
	return /e/i.test(text) && typeof n === 'number' && Number.isFinite(n)
		? n.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
		: text;
};

/**
 * Wraps a raw input value in the KQL value node the field's dataType calls for.
 * Returns undefined when there is nothing to compare against yet (an
 * incomplete row is simply left out of the query).
 */
export function makeValueNode(dataType, comparison, raw, ownByName = null) {
	if (isNullaryOperator(comparison)) return undefined;

	if (isListOperator(comparison)) {
		const entries = (Array.isArray(raw) ? raw : [raw])
			.map(entry => makeValueNode(dataType, 'eq', entry, ownByName))
			.filter(Boolean);
		return entries.length ? makeList(entries) : undefined;
	}

	// A reference is not a literal and takes none of the coercions below: the
	// grammar carries the element NAME, and the API resolves it.
	if (isRefRaw(raw)) {
		return raw.name && refSupportsOperator(dataType, comparison) ? makeRef(raw.name) : undefined;
	}

	if (raw === undefined || raw === null || raw === '') return undefined;

	const kind = fieldKindFor(dataType);
	// A part's value is the canonical literal its picker holds: a weekday's
	// name (`mon`), an hour's or a month's number.
	if (kind === 'datePart') return typeof raw === 'number' && Number.isFinite(raw) ? { type: 'num', v: raw } : { type: 'str', v: String(raw) };
	// The caller, as the grammar's own keyword node — never the string "me",
	// which the parser would read back as text.
	if (ME_KINDS.has(kind) && isMeValue(raw)) return makeMe();
	if (kind === 'user') return { type: 'str', v: String(raw) };
	if (kind === 'number' || kind === 'entryCount') {
		return looksNumeric(raw) ? { type: 'num', v: Number(raw) } : { type: 'str', v: String(raw) };
	}
	if (kind === 'measurement') {
		// The control hands over `{ value, unit }`; it goes out as ONE quoted
		// string in as-entered form (`weight > "20 lb"`) — readable in the URL,
		// safe through the lexer, and exactly what the API's parser reads. A
		// bare number keeps meaning the element's default unit; text the user
		// typed by hand stays as typed, so an unreadable literal can be fixed
		// rather than silently dropped.
		if (typeof raw === 'number') return Number.isFinite(raw) ? { type: 'num', v: raw } : undefined;
		// An own unit (KPR-1109) is written by its id ("2 own:smidgen"), a
		// spelling the API reads for it that a rename never breaks; the chip
		// names it (measurementLabelFor). A row over several elements
		// (`ownByName`, the group's options) writes it by its SINGULAR instead:
		// the one literal goes to every element, and an id means its unit on
		// one element only -- another may spend it on another unit.
		if (isMeasurementValue(raw)) {
			const own = ownByName && isOwnUnitId(raw.unit) ? ownUnitOf(raw.unit, ownByName) : null;
			const text = own ? `${plainNumber(raw.value)} ${own.singular}` : formatEnteredMeasurement(raw);
			return text ? { type: 'str', v: text } : undefined;
		}
		if (typeof raw === 'object') return undefined;
		return { type: 'str', v: String(raw) };
	}
	if (kind === 'currency') {
		// The control hands over `{ amount, currency }`; it goes out as ONE
		// quoted string WITH its code (`price > "20.00 USD"`) — a bare number
		// is refused on a multi-currency element (400 `currency_required`),
		// and a one-currency element's list may grow. A number (a hand-typed
		// `price > 20` read back) and text typed by hand stay as they were,
		// so a query round-trips unchanged and an unreadable literal can be
		// fixed rather than silently dropped.
		if (typeof raw === 'number') return Number.isFinite(raw) ? { type: 'num', v: raw } : undefined;
		if (isCurrencyValue(raw)) {
			const text = currencyLiteral(raw);
			return text ? { type: 'str', v: text } : undefined;
		}
		if (typeof raw === 'object') return undefined;
		return { type: 'str', v: String(raw) };
	}
	if (kind === 'boolean') {
		// The grammar's own bool literal (`paid = true`). The control emits a
		// real boolean; a hand-typed query (or a saved filter from before the
		// control existed) may say yes/no/1/0, which the API's compiler reads
		// the same way.
		if (typeof raw === 'boolean') return { type: 'bool', v: raw };
		const text = String(raw).trim().toLowerCase();
		if (text === 'true' || text === 'yes' || text === '1') return { type: 'bool', v: true };
		if (text === 'false' || text === 'no' || text === '0') return { type: 'bool', v: false };
		return undefined;
	}
	if (kind === 'date') {
		const text = String(raw);
		// Forward relatives (`+7d`, `today+7d`, `now+2h`) are as much a literal
		// as backward ones — "Next 7 days" is asked for as often as "Last 7
		// days" — so the grammar's own reader decides, not a regex kept in step
		// with it by hand.
		if (isRelativeLiteral(text)) return { type: 'rel', v: text };
		// A `time` element's literal is a WALL CLOCK ('13:45'), not a date:
		// the grammar's date literal is `YYYY-MM-DD(THH:mm)` and serializes
		// UNQUOTED, so a clock emitted as one would come back out as
		// `taken >= 13:45` — text the parser cannot read. A quoted string is
		// what the API compares a stored clock against.
		if (dataType === 'time') return { type: 'str', v: text };
		return { type: 'date', v: text };
	}
	if (typeof raw === 'boolean') return { type: 'bool', v: raw };
	return { type: 'str', v: String(raw) };
}

/** The raw editable value behind a value node (what the row's input binds to). */
export function rawValueOf(value) {
	if (!value) return undefined;
	if (value.type === 'list') return value.v.map(entry => rawValueOf(entry));
	if (value.type === 'me') return ME_VALUE;
	// Tagged, so a row's input can tell "the field called qty" from the text
	// "qty" — the one distinction a bare string could not carry.
	if (value.type === 'ref') return makeRefRaw(value.v);
	return value.v;
}

/**
 * Human text for a value, used in chips and the read-only condition list.
 * `resolveLabel(raw)` may turn a stored scalar into its display label (a
 * choice's label for its slug); anything falsy it returns leaves the raw value
 * in place. Lists resolve member by member.
 */
export function displayValue(value, resolveLabel, resolveRefLabel) {
	if (!value) return '';
	if (value.type === 'list') return value.v.map(entry => displayValue(entry, resolveLabel, resolveRefLabel)).join(', ');
	if (value.type === 'rel') return relativeLabel(value.v);
	// A REFERENCE reads as the target element's LABEL, never its slug — but it
	// keeps the braces, because `{{Reorder at}}` is how the rest of the
	// platform already spells "the value of that field" (a formula, a
	// template), and a bare "Reorder at" in a chip would be indistinguishable
	// from somebody having typed those words as text.
	if (value.type === 'ref') {
		const label = typeof resolveRefLabel === 'function' ? resolveRefLabel(value.v) : '';
		return `{{${label === undefined || label === null || label === '' ? value.v : label}}}`;
	}
	// The caller reads as "Me" (a resolver may say who that is).
	if (value.type === 'me') {
		if (typeof resolveLabel === 'function') {
			const label = resolveLabel(ME_VALUE);
			if (label !== undefined && label !== null && label !== '') return String(label);
		}
		return 'Me';
	}
	// A bool chip reads in the element's own words when the caller can supply
	// them ("Paid" / "Unpaid"), and as Yes / No otherwise — never `true`.
	if (value.type === 'bool') {
		if (typeof resolveLabel === 'function') {
			const label = resolveLabel(value.v);
			if (label !== undefined && label !== null && label !== '') return String(label);
		}
		return value.v ? 'Yes' : 'No';
	}
	if (typeof resolveLabel === 'function') {
		const label = resolveLabel(value.v);
		if (label !== undefined && label !== null && label !== '') return String(label);
	}
	return String(value.v);
}

// --------------------------------------------------------------------- rows

let rowSeq = 0;
const nextRowId = () => `row-${++rowSeq}`;

/** A fresh row id, for the modules that build rows of their own (datePresets). */
export const newRowId = () => nextRowId();

/** A fresh, empty row for a field (or the first field offered). */
export function createRow(field) {
	const target = field || SYSTEM_FILTER_FIELDS[0];
	const [firstOperator] = operatorsForField(target);
	return {
		id: nextRowId(),
		path: [...target.path],
		forceElement: !!target.forceElement,
		facet: !!target.facet,
		cmp: firstOperator ? firstOperator.value : 'eq',
		value: undefined,
		...(isEntriesField(target) ? { entryRows: seedEntryRows(target) } : {}),
	};
}

/** The rows a repeating group's row starts with: its first column's, ready to fill in. */
export const seedRowsForGroup = seedEntryRows;

// ------------------------------------------------- repeating groups' rows

/** The repeating group a count's root names (by `#id`, or by name), as a field — or null. */
function entryGroupFieldOf(fields, segment, forceElement = false) {
	if (isElementIdSegment(segment)) {
		const hit = elementIdHit(fields, [segment], 0);
		return hit && hit.def.dataType === REPEATING_GROUP_TYPE ? elementIdField(fields, [segment]) : null;
	}
	const key = pathKey([segment], forceElement);
	return (fields || []).find(field => field.key === key && isEntriesField(field) && !field.facet) || null;
}

/** A group's entry-count field (path and key set by the caller that knows the spelling). */
function entryCountField(group) {
	const path = [...group.path, ENTRY_COUNT];
	return {
		key: pathKey(path, group.forceElement),
		path,
		forceElement: !!group.forceElement,
		label: `${group.label} · number of entries`,
		dataType: ENTRY_COUNT_TYPE,
		badgeType: 'number',
		// Never a one-click chip: a count is asked on purpose, from the builder.
		options: { quickFilter: false },
		cardLabels: group.cardLabels || [],
		cardIds: group.cardIds || [],
		partial: !!group.partial,
		system: false,
		// The group's definitions: the menu offers the count once per definition.
		...(Array.isArray(group.definitions) ? { definitions: group.definitions } : {}),
	};
}

const columnFieldCache = new WeakMap();

/** Is the column named `name` a date holding several dates in an entry (no part of it inside a block)? */
const listDateColumn = (columns, name) => columns.some(column => column.name === name && PARTS_OF_TYPE[column.dataType] && column.options?.allowMultiple === true);

/**
 * The columns of a repeating group, as filter fields in the shape an element
 * field has (so the same rows, value inputs and checks serve them): the group
 * field's own columns this person may read — a column hidden from them is not
 * offered (KPR-688) — each named by its id in a stored filter, and each date
 * or date-time column's day of the week, hour of the day and month, which a
 * block reads as a top-level date's (keepr-api KPR-935: `dose has
 * (given.weekday = mon)`) — but a column holding several dates in an entry,
 * whose part the API refuses inside a block. Not offered: what the grammar
 * refuses inside a block (a reference, a nested group, an entry count).
 */
export function entryColumnFields(group) {
	const columns = columnsOf(group);
	if (!columns.length) return [];
	if (columnFieldCache.has(columns)) return columnFieldCache.get(columns);
	const visible = columns.filter(column => column?.name && !isHiddenFromMe(column));
	const built = visible.length
		? buildFilterFields({ cards: [{ _id: '0'.repeat(24), name: 'Entry', elements: visible }] })
			.filter(field => !field.system && !field.facet && !field.hop && field.dataType !== REPEATING_GROUP_TYPE && field.dataType !== ENTRY_COUNT_TYPE)
			.filter(field => field.dataType !== DATE_PART_TYPE || !listDateColumn(visible, field.path[0]))
		: [];
	columnFieldCache.set(columns, built);
	return built;
}

/**
 * A new row on a repeating group's first column, or null when it offers none.
 * Written as the menu writes it: the column by its id where the group is
 * addressed by its id, by its name where the text keeps names.
 */
export function newEntryRowFor(group) {
	const columns = entryColumnFields(group);
	const [first] = columns;
	if (!first) return null;
	const byId = Array.isArray(group.path) && isElementIdSegment(group.path[0]);
	const [entry] = byId ? elementFieldEntries(first, columns) : [{ field: first }];
	return createRow(entry.field);
}

/** A new row on a repeating group's field starts with its first column's row under it. */
function seedEntryRows(group) {
	const row = newEntryRowFor(group);
	return row ? [row] : [];
}

/** The AST of a `has` row's block: `g has (…)`, or `not g has (…)`; null while no condition under it is complete. */
export function entriesNode(row) {
	const inner = rowsToAst(row.entryRows || []);
	if (!inner) return null;
	const node = makeHas(row.path, inner, { forceElement: !!row.forceElement });
	return row.cmp === 'nhas' ? makeNot(node) : node;
}

/**
 * The rows the inside of a block reads as, or null when it is not an AND of
 * simple conditions on columns (an `or`, a `not`, a nested block — those stay
 * the Advanced tab's). An element group's `or` is NOT read here: `(#a = x or
 * #b = x)` inside a block is two columns, not one element on two cards.
 */
function entryRowsOf(inner) {
	const nodes = inner && inner.op === 'and' && Array.isArray(inner.args) ? inner.args : [inner];
	if (!nodes.every(isConditionNode)) return null;
	return nodes.map(node => ({
		id: nextRowId(),
		path: [...node.path],
		forceElement: !!node.forceElement,
		facet: false,
		cmp: node.cmp,
		value: node.value,
	}));
}

/**
 * A top-level node as a repeating group's row — `g has (…)`, `not g has (…)` —
 * or null. The inside must be an AND of conditions (entryRowsOf).
 */
export function entriesRowOf(node) {
	const negated = !!node && node.op === 'not';
	const block = negated ? node.arg : node;
	if (!isBlock(block) || !Array.isArray(block.path) || block.path.length !== 1) return null;
	const entryRows = entryRowsOf(blockOf(block));
	if (!entryRows) return null;
	return {
		id: nextRowId(),
		path: [...block.path],
		forceElement: !!block.forceElement,
		facet: false,
		cmp: negated ? 'nhas' : 'has',
		value: undefined,
		entryRows,
	};
}

/**
 * One comparison on a row's path: the comparison itself, or — on an element
 * group's segment (`#a|#b`) — the group it stands for.
 */
function rowComparison(path, comparison, value, roots) {
	if (path.length === 1 && isElementGroupSegment(path[0])) return elementGroupNode(elementGroupIds(path[0]), comparison, value);
	return makeCmp(path, comparison, value, roots);
}

/**
 * Row → ONE comparison on the row's own path, for saying it in words — an
 * element group's stand-in (`#a|#b`) included, which `rowToNode` writes out as
 * the group. null when the row is not yet a usable condition.
 */
export function rowCondition(row) {
	if (!row || !Array.isArray(row.path) || !row.path.length || row.kind === SPAN_ROW) return null;
	const roots = { forceElement: !!row.forceElement, facet: !!row.facet };
	// A `has` row in words: the comparison on the group, with its rows beside it.
	if (isEntriesCmp(row.cmp)) {
		const complete = (row.entryRows || []).some(entry => rowNodes(entry).length);
		return complete ? { ...makeCmp(row.path, row.cmp, undefined, roots), entryRows: row.entryRows } : null;
	}
	if (isNullaryOperator(row.cmp)) return makeCmp(row.path, row.cmp, undefined, roots);
	if (!row.value || (row.value.type === 'list' && !row.value.v.length)) return null;
	return makeCmp(row.path, row.cmp, row.value, roots);
}

/** Row → AST node, or null when the row is not yet a usable condition. */
export function rowToNode(row) {
	if (!row || !Array.isArray(row.path) || !row.path.length) return null;
	if (row.kind === SPAN_ROW) return null;
	if (isEntriesCmp(row.cmp)) return entriesNode(row);
	const roots = { forceElement: !!row.forceElement, facet: !!row.facet };
	if (isNullaryOperator(row.cmp)) {
		return rowComparison(row.path, row.cmp, undefined, roots);
	}
	if (!row.value) return null;
	if (row.value.type === 'list' && !row.value.v.length) return null;
	return rowComparison(row.path, row.cmp, row.value, roots);
}

/**
 * A row that stands for a SPAN: one thing the user picked ("This week",
 * "Between…"), two conditions in the query. The row carries its `parts`
 * ([{ cmp, value }, …]) and the preset key that produced them; which presets
 * exist, and how a pair is read back as one row, is `utils/datePresets.js`.
 * This module only knows the shape, so rows and text keep converting in one
 * place.
 */
export const SPAN_ROW = 'span';

export const isSpanRow = row => row?.kind === SPAN_ROW;

/** A span row's parts, complete ones only. */
const spanParts = row => (Array.isArray(row?.parts) ? row.parts : [])
	.filter(part => part && (isNullaryOperator(part.cmp) || part.value));

/** Row → the AST nodes it contributes: one for a plain row, two for a span. */
export function rowNodes(row) {
	if (isSpanRow(row)) {
		if (!Array.isArray(row.path) || !row.path.length) return [];
		const parts = spanParts(row);
		// Half a span is not a filter: "Between Sep 1 and …" must not silently
		// become "everything since Sep 1".
		if (parts.length < 2) return [];
		return parts.map(part => rowComparison(
			row.path,
			part.cmp,
			isNullaryOperator(part.cmp) ? undefined : part.value,
			{ forceElement: !!row.forceElement, facet: !!row.facet }
		));
	}
	const node = rowToNode(row);
	return node ? [node] : [];
}

/** Rows → one AST (AND of every complete row), or null when none is complete. */
export function rowsToAst(rows) {
	const nodes = (rows || []).flatMap(rowNodes);
	if (!nodes.length) return null;
	if (nodes.length === 1) return nodes[0];
	return { op: 'and', args: nodes };
}

/** Rows → canonical KQL text ('' when the builder holds nothing usable). */
export function rowsToText(rows) {
	const ast = rowsToAst(rows);
	return ast ? serialize(ast) : '';
}

/**
 * A node the builder can show as one row?
 *
 * A reference inside an `in (...)` LIST is deliberately excluded. The grammar
 * allows it and the API compiles it, but a list row is edited as a multi-select
 * of literals and there is nowhere in it for "another field" to live — so a
 * hand-typed `status in (open, {{fallback}})` opens on the Advanced tab, where
 * it can be read and edited as the text it is, rather than being silently
 * flattened into something else.
 *
 * A repeating group's block (`ingredient has (name = butter)`, keepr-api
 * KPR-681) is a condition whose value is a whole query on one entry's
 * columns. It is not a flat condition: `astToRows` reads it as a repeating
 * group's row (entriesRowOf), with the rows of its inside beside it, when that
 * inside is an AND of simple conditions; any other block opens on the Advanced
 * tab and is applied as the text it is — never rebuilt from rows, which would
 * lose it.
 */
const isConditionNode = node => {
	if (!node || node.op !== 'cmp') return false;
	if (node.cmp === BLOCK_CMP || isBlock(node)) return false;
	if (node.value && node.value.type === 'list') {
		return !(node.value.v || []).some(member => member && member.type === 'ref');
	}
	return true;
};

/**
 * The AND-of-conditions shape the builder speaks, or null for advanced syntax.
 * An element group (`(#a = x or #b = x)`; its negative, flattened into its
 * siblings) is ONE row, on the group's segment (`#a|#b`).
 */
export function astToRows(ast) {
	if (!ast) return [];
	const whole = readElementGroup(ast);
	let entries;
	if (whole) entries = [{ group: whole }];
	else if (entriesRowOf(ast)) entries = [{ node: ast }];
	else if (isConditionNode(ast)) entries = [{ node: ast }];
	else if (ast.op === 'and' && Array.isArray(ast.args)) {
		entries = groupedConditions(ast.args);
		if (!entries.every(entry => entry.group || isConditionNode(entry.node) || entriesRowOf(entry.node))) return null;
	} else return null;
	return entries.map(({ node, group }) => (group ? {
		id: nextRowId(),
		path: [elementGroupSegment(group.ids)],
		forceElement: false,
		facet: false,
		cmp: group.cmp,
		value: group.value,
	} : (entriesRowOf(node) || {
		id: nextRowId(),
		path: [...node.path],
		forceElement: !!node.forceElement,
		facet: !!node.facet,
		cmp: node.cmp,
		value: node.value,
	})));
}

/** True when the builder can represent this query without losing meaning. */
export const isBuilderShaped = ast => astToRows(ast) !== null;

/** Top-level AND conditions, for chips. null when the query is advanced-shaped. */
export function topLevelConditions(ast) {
	const rows = astToRows(ast);
	return rows === null ? null : rows.map(row => ({
		path: row.path,
		forceElement: row.forceElement,
		facet: row.facet,
		cmp: row.cmp,
		value: row.value,
		// A repeating group's `has`: the conditions under it (KPR-683).
		...(row.entryRows ? { entryRows: row.entryRows } : {}),
	}));
}

/** Drop one top-level condition (chip dismissal). Returns null when none is left. */
export function removeConditionAt(ast, index) {
	return removeConditionsAt(ast, [index]);
}

/**
 * Drop several top-level conditions at once — what a SPAN chip dismisses,
 * since one chip stands for the two conditions that bound it. Indices are
 * positions in `topLevelConditions`.
 */
export function removeConditionsAt(ast, indices) {
	const rows = astToRows(ast);
	if (rows === null) return ast;
	const drop = new Set(indices || []);
	return rowsToAst(rows.filter((_row, i) => !drop.has(i)));
}

/**
 * The display label of a stored choice value, looked up in the field's
 * `options.choices` — undefined for a non-choice field or an unknown value, so
 * callers fall back to the raw text. Chips and the builder's row labels share
 * this so "Parent is Person" reads the same everywhere.
 */
export function choiceLabelFor(field, raw) {
	if (!field || fieldKindOf(field) !== 'choice') return undefined;
	const choices = Array.isArray(field.options?.choices) ? field.options.choices : [];
	const wanted = String(raw);
	// A reference field's key alias (text written before F3) names the same
	// record, read the way the API reads a key — case-insensitively — when
	// exactly one record has it: a key two share is no one's name here.
	const byAlias = choices.filter(choice => (
		choice !== null && typeof choice === 'object' && Array.isArray(choice.aliases)
		&& choice.aliases.some(alias => String(alias).toLowerCase() === wanted.toLowerCase())
	));
	const match = choices.find(choice => (
		choice !== null && typeof choice === 'object'
			? String(choice.value) === wanted
			: String(choice) === wanted
	)) || (byAlias.length === 1 ? byAlias[0] : undefined);
	// A legacy bare-string choice has no separate label to offer.
	if (!match || typeof match !== 'object' || match.label === undefined || match.label === null) return undefined;
	return String(match.label);
}

/** The parser's options for a measurement field: the measure and the unit a bare number means. */
// Every measure an element that allows several reads a literal in (KPR-1085).
const parseOptionsOf = options => ({ measure: options?.measure, measures: options?.measures, ownUnits: options?.ownUnits, defaultUnit: options?.defaultUnit });

/**
 * A measurement literal as the entry it stands for — `{ value, unit }` — or
 * null when the field is not a measurement or the literal does not parse.
 * Mirrors the API compiler: a `num` node means the element's default unit,
 * a string/word goes through the shared parser with the element's measure
 * (so a unit of another measure is unreadable here, as it matches nothing
 * there). Lists never apply: the builder offers no `in` for a quantity.
 */
export function measurementEntryOf(field, value) {
	if (!field || fieldKindOf(field) !== 'measurement') return null;
	if (!value || value.type === 'list') return null;
	if (value.type !== 'num' && value.type !== 'str') return null;
	return readEntryValue(value.v, parseOptionsOf(field.options));
}

/**
 * The as-entered text of a measurement value ("20 lb", "8 lb 7 oz") for a
 * chip or a row label — what the user typed, never the element's display
 * rules. Undefined for a non-measurement field or an unreadable literal, so
 * callers fall back to the raw text.
 */
export function measurementLabelFor(field, raw) {
	if (!field || fieldKindOf(field) !== 'measurement') return undefined;
	if (raw === undefined || raw === null || raw === '') return undefined;
	const node = typeof raw === 'number' ? { type: 'num', v: raw } : { type: 'str', v: String(raw) };
	const entry = measurementEntryOf(field, node);
	return entry ? formatEnteredMeasurement(entry, field.options) || undefined : undefined;
}

/**
 * A currency literal as the entry it stands for — `{ amount, currency }` —
 * or null when the field is not a currency or the literal does not read.
 * Mirrors the API compiler: a `num` node is major units in the element's
 * default currency (the API refuses it outright on a multi-currency element;
 * here it still reads, so the row can show it and be fixed), a string goes
 * through the shared parser with the element's currencies. A fractional
 * minor amount (`20.555`) is a fine bound and is kept.
 */
export function currencyEntryOf(field, value) {
	if (!field || fieldKindOf(field) !== 'currency') return null;
	if (!value || value.type === 'list') return null;
	if (value.type !== 'num' && value.type !== 'str') return null;
	return parseCurrency(value.v, { ...(field.options || {}), fraction: 'round' });
}

/**
 * A currency value in the element's own formatting ("$20.00", "CA$12.50")
 * for a chip or a row label. Undefined for a non-currency field or an
 * unreadable literal, so callers fall back to the raw text.
 */
export function currencyLabelFor(field, raw) {
	if (!field || fieldKindOf(field) !== 'currency') return undefined;
	if (raw === undefined || raw === null || raw === '') return undefined;
	if (isCurrencyValue(raw)) return formatEnteredCurrency(raw, field.options) || undefined;
	const node = typeof raw === 'number' ? { type: 'num', v: raw } : { type: 'str', v: String(raw) };
	const entry = currencyEntryOf(field, node);
	return entry ? formatEnteredCurrency(entry, field.options) || undefined : undefined;
}

/**
 * The display label of a stored value for a field of any kind — a choice's
 * label, a measurement's as-entered text, a yes/no element's own two words —
 * or undefined to keep the raw value. The one resolver chips and the
 * builder's row labels share.
 */
export function conditionValueLabel(field, raw) {
	// A reference names another FIELD, and only the whole vocabulary can turn
	// that into a label — displayValue's `resolveRefLabel` does it.
	if (isRefRaw(raw)) return undefined;
	if (isMeValue(raw) && fieldTakesMe(field?.dataType)) return 'Me';
	const choice = choiceLabelFor(field, raw);
	if (choice !== undefined) return choice;
	if (fieldKindOf(field) === 'boolean' && typeof raw === 'boolean') {
		return formatBooleanValue(field, raw) || undefined;
	}
	if (fieldKindOf(field) === 'currency') return currencyLabelFor(field, raw);
	if (isDatePartField(field)) return datePartLabelFor(field, raw);
	if (fieldKindOf(field) === 'tag') return tagValueLabel(field, raw);
	return measurementLabelFor(field, raw);
}

/**
 * One condition in words: "heart rate > 100", "resident is Molly". `labelFor`
 * turns a path into the element's own label so chips never show raw slugs;
 * the optional `valueLabelFor(path, forceElement, raw)` does the same for the
 * VALUE ("Parent is Person", not "parent is person") — return undefined to
 * keep the raw value. List values are resolved member by member.
 *
 * Both resolvers take the node's `facet` flag as a TRAILING argument
 * (`labelFor(path, forceElement, facet)`, `valueLabelFor(path, forceElement,
 * raw, facet)`), because a facet's key can collide with an element's name and
 * only the flag tells them apart. A resolver that ignores it keeps working.
 */
export function describeCondition(node, labelFor, valueLabelFor, resolveRefLabel, fieldFor) {
	if (!node) return '';
	const label = (typeof labelFor === 'function' && labelFor(node.path, node.forceElement, node.facet)) || node.path.join('.');
	// `fieldFor(node)` — the field descriptor, when the caller has one — lets a
	// list field speak its own words ("Skills has no Welding").
	const field = typeof fieldFor === 'function' ? fieldFor(node) : null;
	// A repeating group's `has an entry where…` (KPR-683): the conditions under
	// it read as the group's COLUMNS (the resolvers are asked with that
	// vocabulary as a last argument), joined, on the ONE entry they share.
	if (isEntriesCmp(node.cmp)) return describeEntries(node, label, field, labelFor, valueLabelFor, resolveRefLabel);
	const operator = operatorLabelFor(field, node.cmp);
	if (isNullaryOperator(node.cmp)) return `${label} ${operator}`;
	// What follows `contains` is text somebody typed, not a stored choice: a
	// substring that happens to equal a choice's value is not that choice.
	const substring = isTextMatchOperator(node.cmp) && !!field && fieldKindOf(field) === 'choice';
	const resolve = typeof valueLabelFor === 'function' && !substring
		? raw => valueLabelFor(node.path, node.forceElement, raw, node.facet)
		: undefined;
	return `${label} ${operator} ${displayValue(node.value, resolve, resolveRefLabel)}`;
}

/** `Ingredient has an entry where Name is butter and Qty > 2` — the conditions under a repeating group's `has`. */
function describeEntries(node, label, field, labelFor, valueLabelFor, resolveRefLabel) {
	const columns = field ? entryColumnFields(field) : [];
	const columnLabel = (path, forceElement) => findField(columns, path, forceElement).label;
	const columnValue = (path, forceElement, raw) => {
		const found = findField(columns, path, forceElement);
		// A caller that can name what an id stands for (a lookup column's item)
		// is asked with the column's own vocabulary.
		const supplied = typeof valueLabelFor === 'function' ? valueLabelFor(path, forceElement, raw, false, columns) : undefined;
		return supplied !== undefined && supplied !== null && supplied !== '' ? supplied : conditionValueLabel(found, raw);
	};
	const parts = (node.entryRows || [])
		.map(row => rowCondition(row))
		.filter(Boolean)
		.map(condition => describeCondition(condition, columnLabel, columnValue, resolveRefLabel, c => findField(columns, c.path, c.forceElement)));
	return `${label} ${OPERATOR_LABELS[node.cmp]}${parts.length ? ` ${parts.join(' and ')}` : ''}`;
}

/**
 * A FACET condition in words. A facet is not a field somebody compared — it is
 * a thing the list is narrowed TO — so the positive forms read like the chip
 * they are ("People: Alice", "People: Alice, Bob"), the same colon a period
 * chip wears. The negatives keep their operator, because "People: Alice" for
 * `!= Alice` would say the opposite of what it means.
 *
 * `resolveValue(raw)` is the title resolver the caller already has (a lookup
 * value is an id; the chip has to say the name).
 */
export function describeFacetCondition(field, node, resolveValue) {
	const label = field?.shortLabel || field?.label || (node?.path || []).join('.');
	if (isNullaryOperator(node?.cmp)) return `${label} ${OPERATOR_LABELS[node.cmp] || node.cmp}`;
	const text = displayValue(node?.value, resolveValue);
	if (node?.cmp === 'eq' || node?.cmp === 'in') return `${label}: ${text}`;
	return `${label} ${OPERATOR_LABELS[node?.cmp] || node?.cmp} ${text}`;
}

// -------------------------------------------------------------- search box

/**
 * The toolbar search box is sugar over ONE condition: `text ~ "term"` at the
 * top level of the query. These two helpers are how a list page reads and
 * rewrites it without disturbing the rest of the query (which the chips and
 * the builder own). "Top level" means the root `cmp` itself or a direct child
 * of a root `and` — a search term nested under or/not is the builder's, not
 * the box's.
 */
const isSearchCondition = (node, path) =>
	!!node
	&& node.op === 'cmp'
	&& node.cmp === 'contains'
	&& Array.isArray(node.path)
	&& node.path.length === 1
	&& node.path[0] === path
	// A QUOTED `"text"` names an element called text, never the search
	// (keepr-api KPR-739: `text` is the items subject's free-text term).
	&& !node.forceElement;

export const topLevelNodes = ast => {
	if (!ast) return [];
	if (ast.op === 'and' && Array.isArray(ast.args)) return ast.args;
	return [ast];
};

/** The term in the first top-level `<path> ~ …` condition, or '' when there is none. */
export function readSearchTerm(ast, path = 'text') {
	const hit = topLevelNodes(ast).find(node => isSearchCondition(node, path));
	if (!hit || !hit.value || hit.value.v === undefined || hit.value.v === null) return '';
	return String(hit.value.v);
}

/**
 * The query with every top-level `<path> ~ …` condition removed and, when
 * `term` is non-blank, `<path> ~ "term"` appended to the root AND (a non-AND
 * root is wrapped). Returns null when nothing remains. Pure: the input AST is
 * never mutated.
 */
export function withSearchTerm(ast, term, path = 'text') {
	const trimmed = term === undefined || term === null ? '' : String(term).trim();
	const kept = topLevelNodes(ast).filter(node => !isSearchCondition(node, path));
	if (trimmed) kept.push(makeCmp([path], 'contains', makeStr(trimmed)));
	if (!kept.length) return null;
	if (kept.length === 1) return kept[0];
	return makeAnd(kept);
}

/**
 * The same sugar for ONE top-level `<path> = value` condition — the parent
 * list's Sub-collection select writes `collection = <id>` into `q` this way
 * (docs/plans/sub-collections.md §5.5), and reads it back to show its value.
 * A value the select did not write (an `in`, a negation, one nested under
 * or/not) belongs to the builder and reads as nothing here.
 */
const isEqualsCondition = (node, path) =>
	!!node
	&& node.op === 'cmp'
	&& node.cmp === 'eq'
	&& Array.isArray(node.path)
	&& node.path.length === 1
	&& node.path[0] === path;

/** The value of the first top-level `<path> = …` condition, or '' when there is none. */
export function readEqualsTerm(ast, path) {
	const hit = topLevelNodes(ast).find(node => isEqualsCondition(node, path));
	if (!hit || !hit.value || hit.value.v === undefined || hit.value.v === null) return '';
	return String(hit.value.v);
}

/**
 * The query with every top-level `<path> = …` condition removed and, when
 * `value` is non-blank, `<path> = "value"` appended; null when nothing
 * remains. Pure.
 */
export function withEqualsTerm(ast, value, path) {
	const trimmed = value === undefined || value === null ? '' : String(value).trim();
	const kept = topLevelNodes(ast).filter(node => !isEqualsCondition(node, path));
	if (trimmed) kept.push(makeCmp([path], 'eq', makeStr(trimmed)));
	if (!kept.length) return null;
	if (kept.length === 1) return kept[0];
	return makeAnd(kept);
}

/**
 * The `collection` system field at a PARENT collection's list (keepr-api §
 * Sub-collections): the family as choices — the collection itself, then each
 * sub-collection the caller can read — by id, so the chip reads the name.
 * `collection = <parent>` is the whole family (a collection and everything
 * beneath it); the list's own rows alone are `?scope=own`. Null for a
 * collection with no sub-collections: the field would have one answer.
 */
export function collectionFamilyFilterField(family = []) {
	const choices = (family || [])
		.map(member => {
			const value = normalizeId(member?._id);
			return value ? { value, label: member?.name || value } : null;
		})
		.filter(Boolean);
	if (choices.length < 2) return null;
	// `this` is the scoping collection alone — a word the API reads here
	// beside the ids and names, so the builder's value check accepts it
	// (utils/idValueValidation.js, `keywords`).
	return { key: 'collection', path: ['collection'], label: 'Collection', dataType: 'choice', options: { choices, keywords: ['this'] }, system: true };
}

/**
 * Re-shapes a row's value when its operator changes: `in` needs a list, the
 * value comparisons need a single value, and `is empty` needs none.
 */
export function retargetValue(row, nextCmp, dataType) {
	if (isNullaryOperator(nextCmp)) return undefined;
	const raw = rawValueOf(row?.value);
	if (raw === undefined) return undefined;
	// A reference survives only where the new operator still has a reading for
	// a field-to-field comparison; `is empty`, `is any of` and the operators
	// the family refuses drop it rather than carrying a dangling target.
	if (isRefRaw(raw)) {
		return isListOperator(nextCmp) ? undefined : makeValueNode(dataType, nextCmp, raw);
	}
	if (isListOperator(nextCmp)) {
		return makeValueNode(dataType, nextCmp, Array.isArray(raw) ? raw : [raw]);
	}
	return makeValueNode(dataType, nextCmp, Array.isArray(raw) ? raw[0] : raw);
}

/**
 * Parse for the UI: never throws. `{ ast, rows, error }` — `rows` is null when
 * the query parses but is not AND-of-conditions (the builder goes read-only),
 * `error` carries the parser's message and character position.
 */
export function parseForBuilder(text) {
	const trimmed = (text || '').trim();
	if (!trimmed) return { ast: null, rows: [], error: null };
	try {
		const ast = parse(trimmed);
		return { ast, rows: astToRows(ast), error: null };
	} catch (err) {
		return {
			ast: null,
			rows: null,
			error: {
				message: err?.message || 'This query could not be read.',
				position: Number.isInteger(err?.position) ? err.position : 0,
			},
		};
	}
}

/** Canonical text for a query the user typed (or '' when it does not parse). */
export function canonicalize(text) {
	const { ast } = parseForBuilder(text);
	return ast ? serialize(ast) : '';
}
